import {
  normalizeTargetCycles, resolveTargetCycleForYear,
} from '../components/targetCycleModel';
import {
  ACTION_TYPES, normalizeRecommendationFinance,
} from './costRateModel';
import {
  RETIREMENT_BATCH_STATUS, normalizeRetirementBatches, retirementBatchSummary,
  normalizeRetirementBatch, rejectRetirementBatch, submitRetirementBatch,
  updateRetirementBatch, validateRetirementBatch, verifyRetirementBatch,
} from './retirementBatchModel';
import {
  DELIVERY_STATUS, DELIVERY_STATUS_TRANSITIONS, EVIDENCE_STATUS, canonicalDeliveryStatus,
} from './actionStatusModel';

export const CLIMATE_ACTION_GROUPS = {
  SCOPE_LINKED: 'scope-linked',
  OTHER: 'other',
};

export const CLIMATE_ACTION_TYPES = ACTION_TYPES;

/** Legacy export retained for read compatibility only. New actions persist actionType. */
export const CLIMATE_ACTION_KINDS = Object.freeze({
  REGULAR: ACTION_TYPES.CLIMATE_ACTION,
  RETIRED_CARBON_CREDIT: ACTION_TYPES.CARBON_CREDIT_RETIREMENT,
});

export const CLIMATE_ACTION_SCOPES = {
  TOTAL: 'total',
  SCOPE_1: 'scope1',
  SCOPE_2: 'scope2',
  SCOPE_3: 'scope3',
};

export const DEMO_YEARS = [2026, 2025, 2024];
export const DEFAULT_DEMO_YEAR = 2025;
export const ELECTRICITY_FACTOR_TCO2E_PER_KWH = 0.000421;

export const IMPACT_RECORD_TYPES = Object.freeze({
  ESTIMATED: 'ESTIMATED',
  REPORTED: 'REPORTED',
  VERIFIED: 'VERIFIED',
});

export const CLIMATE_ACTION_WORKFLOW_STATUS = Object.freeze({
  ...DELIVERY_STATUS,
  PROPOSED: 'PROPOSED',
  APPROVED: 'APPROVED',
  EVIDENCE_PENDING: 'EVIDENCE_PENDING',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
});

export const CLIMATE_ACTION_VERIFICATION_STATUS = Object.freeze({
  ...EVIDENCE_STATUS,
  NOT_YET_VERIFIED: 'NOT_YET_VERIFIED',
});

export const CLIMATE_ACTION_REVIEW_STATUS = Object.freeze({
  PENDING_REVIEW: 'PENDING_REVIEW',
  APPROVED: 'APPROVED',
  NOT_APPROVED: 'NOT_APPROVED',
});

export const ACCOUNTING_CLASSIFICATIONS = Object.freeze({
  SCOPE_LINKED_REDUCTION: 'SCOPE_LINKED_REDUCTION',
  AVOIDED_EMISSIONS: 'AVOIDED_EMISSIONS',
  CLIMATE_CONTRIBUTION: 'CLIMATE_CONTRIBUTION',
  CARBON_REMOVAL: 'CARBON_REMOVAL',
  RETIRED_CARBON_CREDIT: 'RETIRED_CARBON_CREDIT',
});

export const RECOMMENDATION_SCOPE_GROUPS = Object.freeze({
  ALL: 'all',
  SCOPE_1: 'scope1',
  SCOPE_2: 'scope2',
  SCOPE_3: 'scope3',
  OTHER: 'other',
});

export const ACTIVITY_INPUT_MODES = Object.freeze({
  ACTIVITY_CHANGE: 'ACTIVITY_CHANGE',
  TARGET_TCO2E: 'TARGET_TCO2E',
});

export const FINANCIAL_READINESS = Object.freeze({
  AVAILABLE: 'AVAILABLE',
  COST_DATA_REQUIRED: 'COST_DATA_REQUIRED',
});

export const TARGET_TRAJECTORY_TYPES = Object.freeze({
  LINEAR: 'linear',
  CUSTOM: 'custom',
});

const MANUAL_INITIAL_STATUSES = new Set(['DRAFT', 'IN_PROGRESS']);
const PRESENTATION_STATUS = Object.freeze({
  DRAFT: 'saved',
  IN_PROGRESS: 'inProgress',
  COMPLETED: 'completed',
  NOT_APPROVED: 'notApproved',
});
const IMPACT_STAGE_ORDER = Object.freeze([
  IMPACT_RECORD_TYPES.VERIFIED,
  IMPACT_RECORD_TYPES.REPORTED,
  IMPACT_RECORD_TYPES.ESTIMATED,
]);
export const DEMO_INVENTORIES = [
  { year: 2015, scope1: 70, scope2: 450, scope3: 21000 },
  { year: 2021, scope1: 50.4, scope2: 369.6, scope3: 19430 },
  { year: 2022, scope1: 48.6, scope2: 356.4, scope3: 18805 },
  { year: 2023, scope1: 46.56, scope2: 341.44, scope3: 18252 },
  { year: 2024, scope1: 43.44, scope2: 318.56, scope3: 17618 },
  { year: 2025, scope1: 29.97, scope2: 390.87, scope3: 16820 },
];

export const DEMO_TARGETS = {
  scope1: {
    scope: 'scope1', trajectoryType: 'linear', baselineYear: 2025,
    targetYear: 2030, reductionPercent: 30, targetYearEmissions: 20.979,
  },
  scope2: {
    scope: 'scope2', trajectoryType: 'linear', baselineYear: 2025,
    targetYear: 2030, reductionPercent: 50, targetYearEmissions: 195.435,
  },
  scope3: {
    scope: 'scope3', trajectoryType: 'linear', baselineYear: 2025,
    targetYear: 2030, reductionPercent: 45, targetYearEmissions: 9251,
  },
};

/**
 * Deterministic planning metric owned by the carbon/target model, never by LLM prose.
 * The denominator is the remaining reduction from the inventory snapshot to the
 * configured final target for the same Scope.
 */
export function calculateTargetGapCoveragePercent({
  baselineEmissionsTco2e, benefitTco2e, targetEmissionsTco2e,
}) {
  if (![baselineEmissionsTco2e, benefitTco2e, targetEmissionsTco2e]
    .every(value => typeof value === 'number' && Number.isFinite(value))) {
    return null;
  }
  const baseline = baselineEmissionsTco2e;
  const benefit = benefitTco2e;
  const target = targetEmissionsTco2e;
  const targetGap = baseline - target;
  if (benefit < 0 || targetGap <= 0) {
    return null;
  }
  return (benefit / targetGap) * 100;
}

const FY2025_SCOPE2_TARGET_GAP_INPUT = Object.freeze({
  baselineEmissionsTco2e: DEMO_INVENTORIES.find(item => item.year === 2025)?.scope2,
  targetEmissionsTco2e: DEMO_TARGETS.scope2.targetYearEmissions,
});

export const SOLUTION_CATALOG = {
  green_electricity: {
    group: CLIMATE_ACTION_GROUPS.SCOPE_LINKED,
    scope: 'scope2',
    fieldKeys: ['office', 'renewableKwh', 'period', 'certificateReference'],
    requiredFieldKeys: ['office', 'renewableKwh', 'period', 'certificateReference'],
  },
  business_travel: {
    group: CLIMATE_ACTION_GROUPS.SCOPE_LINKED,
    scope: 'scope3Cat6',
    fieldKeys: ['office', 'activityValue', 'period', 'evidenceReference'],
    requiredFieldKeys: ['activityValue', 'period'],
  },
  fleet_ev: {
    group: CLIMATE_ACTION_GROUPS.SCOPE_LINKED,
    scope: 'scope1',
    fieldKeys: ['office', 'activityValue', 'period', 'evidenceReference'],
    requiredFieldKeys: ['activityValue', 'period'],
  },
  hvac_efficiency: {
    group: CLIMATE_ACTION_GROUPS.SCOPE_LINKED,
    scope: 'scope2',
    fieldKeys: ['office', 'activityValue', 'period', 'evidenceReference'],
    requiredFieldKeys: ['office', 'activityValue', 'period'],
  },
  laptop_reuse: {
    group: CLIMATE_ACTION_GROUPS.SCOPE_LINKED,
    scope: 'scope3Cat5',
    fieldKeys: ['reusedQuantity', 'weightKg', 'disposalBaseline', 'period', 'evidenceReference'],
    requiredFieldKeys: ['reusedQuantity', 'weightKg', 'disposalBaseline', 'period'],
  },
  device_life_extension: {
    group: CLIMATE_ACTION_GROUPS.SCOPE_LINKED,
    scope: 'scope3Cat5',
    fieldKeys: ['reusedQuantity', 'period', 'evidenceReference'],
    requiredFieldKeys: ['reusedQuantity', 'period'],
  },
  virtual_meeting_policy: {
    group: CLIMATE_ACTION_GROUPS.SCOPE_LINKED,
    scope: 'scope3Cat6',
    fieldKeys: ['office', 'activityValue', 'period', 'evidenceReference'],
    requiredFieldKeys: ['activityValue', 'period'],
  },
  afforestation: {
    group: CLIMATE_ACTION_GROUPS.OTHER,
    scope: 'contribution',
    fieldKeys: ['treeCount', 'location', 'period', 'methodologyReference'],
    requiredFieldKeys: ['treeCount', 'location', 'period', 'methodologyReference'],
  },
  dhl_gogreen: {
    group: CLIMATE_ACTION_GROUPS.OTHER,
    scope: 'mappingPending',
    fieldKeys: ['allocatedTco2e', 'period', 'certificateReference'],
    requiredFieldKeys: ['allocatedTco2e', 'period', 'certificateReference'],
  },
  green_kitchen: {
    group: CLIMATE_ACTION_GROUPS.OTHER,
    scope: 'contribution',
    fieldKeys: ['activityValue', 'period', 'methodologyReference'],
    requiredFieldKeys: ['activityValue', 'period', 'methodologyReference'],
  },
  retired_offset: {
    group: CLIMATE_ACTION_GROUPS.OTHER,
    scope: 'retiredOffset',
    fieldKeys: ['registryReference', 'retiredQuantity', 'retirementDate'],
    requiredFieldKeys: ['registryReference', 'retiredQuantity', 'retirementDate'],
  },
};

export const DEMO_TARGET_CYCLES = [
  {
    id: 'cycle-2025-2030',
    baseYear: 2025,
    targetYear: 2030,
    status: 'WORKING',
  },
  {
    id: 'cycle-2015-2020',
    baseYear: 2015,
    targetYear: 2020,
    status: 'ARCHIVED',
    targets: {
      scope1: {
        scope: 'scope1', trajectoryType: 'linear', baselineYear: 2015,
        targetYear: 2020, reductionPercent: 10, targetYearEmissions: 63,
      },
      scope2: {
        scope: 'scope2', trajectoryType: 'linear', baselineYear: 2015,
        targetYear: 2020, reductionPercent: 15, targetYearEmissions: 382.5,
      },
      scope3: {
        scope: 'scope3', trajectoryType: 'linear', baselineYear: 2015,
        targetYear: 2020, reductionPercent: 12, targetYearEmissions: 18480,
      },
    },
  },
];

export const ACTIVITY_DRIVER_CATALOG = Object.freeze({
  scope1Fuel: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.SCOPE_1,
    category: 'fuel',
    nativeUnit: 'L',
    emissionFactorTco2ePerUnit: 0.0022901266666666666,
    factorDisplay: '2.2901266667 tCO₂e/kL',
    factorSource: 'Carbon Insight emission factor snapshot · FY2025 · GASOLINE',
    financialDriver: 'unit_price_jpy_per_litre',
  }),
  scope1Diesel: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.SCOPE_1,
    category: 'DIESEL',
    nativeUnit: 'L',
    emissionFactorTco2ePerUnit: null,
    factorRequirement: 'boundaryFactor',
    factorSource: 'A FY/category-specific DIESEL factor snapshot is required',
    financialDriver: 'unit_price_jpy_per_litre',
  }),
  scope2Electricity: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.SCOPE_2,
    category: 'electricity',
    nativeUnit: 'kWh',
    emissionFactorTco2ePerUnit: ELECTRICITY_FACTOR_TCO2E_PER_KWH,
    factorDisplay: '0.000421 tCO₂e/kWh',
    factorSource: 'Carbon Insight emission factor snapshot · FY2025 · demo office',
    financialDriver: 'tariff_premium_jpy_per_kwh',
  }),
  scope3Cat1_1: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.SCOPE_3,
    category: 'cat1',
    subcategory: 'cat1.1',
    nativeUnit: 'JPY',
    emissionFactorTco2ePerUnit: 0.0000013735119067981292,
    factorDisplay: '1.3735119068 tCO₂e/million JPY',
    factorSource: 'Carbon Insight emission factor snapshot · FY2025 · CAT_1_1',
    financialDriver: 'implementation_cost_jpy',
    isSpendEquivalent: true,
  }),
  scope3Cat2: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.SCOPE_3,
    category: 'cat2',
    nativeUnit: 'JPY',
    emissionFactorTco2ePerUnit: 0.0000022755006323306297,
    factorDisplay: '2.2755006323 tCO₂e/million JPY',
    factorSource: 'Carbon Insight emission factor snapshot · FY2025 · CAT_2',
    financialDriver: 'implementation_cost_jpy',
    isSpendEquivalent: true,
  }),
  scope3Cat3: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.SCOPE_3,
    category: 'cat3',
    nativeUnit: 'kWh',
    emissionFactorTco2ePerUnit: 0.0000682203389876269,
    factorDisplay: '0.0682203390 kgCO₂e/kWh',
    factorSource: 'Carbon Insight emission factor snapshot · FY2025 · CAT_3',
    financialDriver: 'unit_price_jpy_per_kwh',
  }),
  scope3Cat5: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.SCOPE_3,
    category: 'cat5',
    nativeUnit: 'kg',
    emissionFactorTco2ePerUnit: null,
    factorRequirement: 'wasteType',
    financialDriver: 'treatment_cost_jpy_per_kg',
  }),
  scope3Cat6: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.SCOPE_3,
    category: 'cat6',
    nativeUnit: 'JPY',
    allowedNativeUnits: Object.freeze(['JPY', 'NIGHT']),
    factorRequirement: 'travelMode',
    financialDriver: 'alternative_travel_cost_jpy',
  }),
  scope3Cat7: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.SCOPE_3,
    category: 'cat7',
    nativeUnit: 'JPY',
    allowedNativeUnits: Object.freeze(['JPY']),
    factorRequirement: 'commuteMode',
    financialDriver: 'commuting_program_cost_jpy',
  }),
  scope3Cat8: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.SCOPE_3,
    category: 'cat8',
    nativeUnit: 'm²',
    emissionFactorTco2ePerUnit: 0.0824379007471962,
    factorDisplay: '0.0824379007 tCO₂e/m²/year',
    factorSource: 'Carbon Insight emission factor snapshot · FY2025 · CAT_8',
    financialDriver: 'project_cost_jpy_per_m2',
  }),
  retiredCarbonCredit: Object.freeze({
    scopeGroup: RECOMMENDATION_SCOPE_GROUPS.OTHER,
    category: 'retiredCarbonCredit',
    nativeUnit: 'tCO2e',
    emissionFactorTco2ePerUnit: 1,
    financialDriver: 'credit_price_jpy_per_tco2e',
  }),
});

const SOLUTION_PRESENTATION_METADATA = Object.freeze({
  green_electricity: Object.freeze({ scopeGroup: 'scope2', category: 'electricity' }),
  business_travel: Object.freeze({ scopeGroup: 'scope3', category: 'cat6' }),
  fleet_ev: Object.freeze({ scopeGroup: 'scope1', category: 'fuel' }),
  hvac_efficiency: Object.freeze({ scopeGroup: 'scope2', category: 'electricity' }),
  laptop_reuse: Object.freeze({ scopeGroup: 'scope3', category: 'cat5' }),
  device_life_extension: Object.freeze({ scopeGroup: 'scope3', category: 'cat5' }),
  virtual_meeting_policy: Object.freeze({ scopeGroup: 'scope3', category: 'cat6' }),
  afforestation: Object.freeze({ scopeGroup: 'other', category: 'climateContribution' }),
  dhl_gogreen: Object.freeze({ scopeGroup: 'other', category: 'mappingPending' }),
  green_kitchen: Object.freeze({ scopeGroup: 'other', category: 'climateContribution' }),
  retired_offset: Object.freeze({ scopeGroup: 'other', category: 'retiredCarbonCredit' }),
});

/** Return stable grouping and accounting metadata for one catalog solution. */
export function solutionMetadata(solutionCode) {
  const presentation = SOLUTION_PRESENTATION_METADATA[solutionCode] || {};
  const catalogEntry = SOLUTION_CATALOG[solutionCode];
  let accountingClassification = ACCOUNTING_CLASSIFICATIONS.CLIMATE_CONTRIBUTION;
  if (catalogEntry?.group === CLIMATE_ACTION_GROUPS.SCOPE_LINKED) {
    accountingClassification = ACCOUNTING_CLASSIFICATIONS.SCOPE_LINKED_REDUCTION;
  }
  if (solutionCode === 'retired_offset') {
    accountingClassification = ACCOUNTING_CLASSIFICATIONS.RETIRED_CARBON_CREDIT;
  }
  return {
    scopeGroup: presentation.scopeGroup || RECOMMENDATION_SCOPE_GROUPS.OTHER,
    category: presentation.category || 'unmapped',
    accountingClassification,
  };
}

export const DEMO_ACTIONS = [
  {
    id: 'action-green-electricity', solutionCode: 'green_electricity', source: 'existing',
    solutionName: 'Switch Mita Office to certified renewable electricity',
    scopeGroup: 'scope2', category: 'electricity',
    accountingClassification: 'SCOPE_LINKED_REDUCTION',
    reviewStatus: 'APPROVED', isApprovedForCompensation: true,
    requestedForCompensationReview: false,
    plannedActionKey: 'greenMita',
    status: 'inProgress', readiness: 'ready', updatedAt: '2025-08-02',
    fiscalYear: 2025,
    annualResults: [{
      fiscalYear: 2025, estimatedImpactTco2e: 2500, reportedImpactTco2e: 2500,
      verifiedImpactTco2e: 2500, verificationStatus: 'VERIFIED',
      annualCostJpy: 11876000, annualSavingsJpy: 4200000,
      evidenceRecords: [{
        id: 'evidence-green-2025', name: 'Mita-renewable-certificate-FY2025.pdf',
        type: 'application/pdf', sizeBytes: 850000, reference: 'RE-MITA-FY2025',
        evidenceType: 'CERTIFICATE', status: 'VERIFIED',
      }],
    }],
    fieldValues: { office: 'Mita Office', renewableKwh: '5938242', period: 'FY2025', certificateReference: 'RE-MITA-FY2025' },
  },
  {
    id: 'action-green-electricity-2024', solutionCode: 'green_electricity', source: 'existing',
    solutionName: 'Switch Mita Office to certified renewable electricity',
    scopeGroup: 'scope2', category: 'electricity',
    accountingClassification: 'SCOPE_LINKED_REDUCTION',
    reviewStatus: 'APPROVED', isApprovedForCompensation: true,
    requestedForCompensationReview: false,
    plannedActionKey: 'greenMita',
    status: 'inProgress', readiness: 'needsEvidence', updatedAt: '2024-08-02',
    fiscalYear: 2024,
    annualResults: [{
      fiscalYear: 2024, estimatedImpactTco2e: 140, reportedImpactTco2e: 140,
      verifiedImpactTco2e: null, verificationStatus: 'UNDER_REVIEW',
      evidenceRecords: [],
    }],
    fieldValues: { office: 'Mita Office', renewableKwh: '332542', period: 'FY2024', certificateReference: '' },
  },
  {
    id: 'action-business-travel', solutionCode: 'business_travel', source: 'existing',
    solutionName: 'Replace eligible short-haul flights with rail and trip consolidation',
    scopeGroup: 'scope3', category: 'cat6',
    accountingClassification: 'SCOPE_LINKED_REDUCTION',
    reviewStatus: 'APPROVED', isApprovedForCompensation: true,
    requestedForCompensationReview: false,
    plannedActionKey: 'travelPolicy',
    status: 'inProgress', readiness: 'ready', updatedAt: '2025-08-18',
    fiscalYear: 2025,
    annualResults: [{
      fiscalYear: 2025, estimatedImpactTco2e: 1200, reportedImpactTco2e: 1200,
      verifiedImpactTco2e: 1200, verificationStatus: 'VERIFIED',
      annualCostJpy: 9200000, annualSavingsJpy: 14500000,
      evidenceRecords: [{
        id: 'evidence-travel-2025', name: 'Travel-activity-FY2025.xlsx',
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        sizeBytes: 420000, reference: 'TRAVEL-POLICY-FY2025',
        evidenceType: 'ACTIVITY_REPORT', status: 'VERIFIED',
      }],
    }],
    fieldValues: { office: 'All offices', activityValue: '1200', period: 'FY2025', evidenceReference: 'TRAVEL-POLICY-FY2025' },
  },
  {
    id: 'action-laptop-reuse', solutionCode: 'laptop_reuse', source: 'existing',
    solutionName: 'Reuse eligible laptops before WEEE disposal',
    scopeGroup: 'scope3', category: 'cat5',
    accountingClassification: 'SCOPE_LINKED_REDUCTION',
    reviewStatus: 'PENDING_REVIEW', isApprovedForCompensation: false,
    requestedForCompensationReview: false,
    boundary: 'All offices',
    plannedActionKey: 'laptopProgram',
    status: 'saved', readiness: 'needsData', updatedAt: '2025-08-15',
    fiscalYear: 2025, annualResults: [],
    fieldValues: { reusedQuantity: '', weightKg: '', disposalBaseline: '', period: 'FY2025' },
  },
  {
    id: 'action-afforestation', solutionCode: 'afforestation', source: 'manual',
    solutionName: 'Complete methodology and evidence for the afforestation program',
    scopeGroup: 'other', category: 'climateContribution',
    accountingClassification: 'CLIMATE_CONTRIBUTION',
    reviewStatus: 'APPROVED', isApprovedForCompensation: true,
    requestedForCompensationReview: false,
    plannedActionKey: 'afforestationVerification',
    status: 'inProgress', readiness: 'needsEvidence', updatedAt: '2025-07-28',
    fiscalYear: 2025,
    annualResults: [{
      fiscalYear: 2025, estimatedImpactTco2e: 700, reportedImpactTco2e: 700,
      verifiedImpactTco2e: null, verificationStatus: 'UNDER_REVIEW',
      annualCostJpy: 17500000, annualSavingsJpy: null,
      evidenceRecords: [{
        id: 'evidence-forest-2025', name: 'Afforestation-monitoring-FY2025.pdf',
        type: 'application/pdf', sizeBytes: 1200000, reference: 'FOREST-METHOD-FY2025',
        evidenceType: 'METHODOLOGY', status: 'UNDER_REVIEW',
      }],
    }],
    fieldValues: { treeCount: '1357', location: 'Japan', period: 'FY2025', methodologyReference: 'FOREST-METHOD-FY2025' },
  },
  {
    id: 'action-dhl', solutionCode: 'dhl_gogreen', source: 'manual',
    solutionName: 'Collect the GoGreen Plus certificate and map allocated impact',
    scopeGroup: 'other', category: 'mappingPending',
    accountingClassification: 'CLIMATE_CONTRIBUTION',
    reviewStatus: 'PENDING_REVIEW', isApprovedForCompensation: false,
    requestedForCompensationReview: false,
    plannedActionKey: 'dhlMapping',
    status: 'saved', readiness: 'needsData', updatedAt: '2025-07-15',
    fiscalYear: 2025, annualResults: [],
    fieldValues: { allocatedTco2e: '', period: 'FY2025', certificateReference: '' },
  },
  {
    id: 'action-retired-offset', solutionCode: 'retired_offset', source: 'manual',
    solutionName: 'Retire 620 tCO2e through approved registries',
    actionType: ACTION_TYPES.CARBON_CREDIT_RETIREMENT,
    scope: 'scope2', scopeGroup: 'scope2', category: 'electricity',
    subcategory: null, boundary: 'Company-wide electricity procurement',
    accountingClassification: 'RETIRED_CARBON_CREDIT',
    plannedActionKey: 'offsetRetirement',
    status: 'inProgress', readiness: 'needsEvidence', updatedAt: '2025-08-20',
    fiscalYear: 2025,
    annualResults: [{
      fiscalYear: 2025, estimatedImpactTco2e: 620, reportedImpactTco2e: 620,
      verifiedImpactTco2e: 400, verificationStatus: 'UNDER_REVIEW',
      financialEstimate: {
        status: 'AVAILABLE', estimateKind: 'RETIREMENT', targetTco2e: 620,
        annualEffectType: 'ADDITIONAL_COST', annualAmountJpy: null,
        retirementCostJpy: 1860000, implementationCostJpy: null,
        firstYearNetEffectJpy: -1860000, costOrBenefitPerTco2eJpy: 3000,
        costRateSnapshot: {
          id: 'demo-retired-credit-v1', source: 'DEMO_COST_RATE_WORKBOOK_V1',
          version: '1', currency: 'JPY', rateBasis: 'RETIRED_TCO2E',
          priceJpyPerTco2e: 3000,
          approvalStatus: 'DEMO_APPROVED', effectiveFromFY: 2025, effectiveToFY: 2025,
        },
      },
      evidenceRecords: [],
      retirementBatches: [
        {
          id: 'retirement-batch-a', quantityTco2e: 400,
          registry: 'Verra Registry', standard: 'Verra VCS',
          projectReference: 'VCS-PROJECT-2022', retirementReference: 'REG-RET-2025-001',
          serialRange: 'VCS-2022-LOT-A', vintage: '2022', retirementDate: '2025-08-01',
          beneficiary: 'Carbon Insight internal account', creditPriceJpyPerTco2e: 3000,
          priceSource: 'DEMO_COST_RATE_WORKBOOK_V1', verificationStatus: 'VERIFIED',
          evidenceRecords: [{
            id: 'evidence-credit-verified-2025', name: 'Registry-retirement-400-FY2025.pdf',
            type: 'application/pdf', sizeBytes: 640000, reference: 'REG-RET-2025-001',
            evidenceType: 'RETIREMENT_CERTIFICATE', status: 'VERIFIED',
          }],
          audit: [{
            type: 'VERIFIED_AND_INCLUDED_IN_BALANCE', actor: 'Demo Admin',
            occurredAt: '2025-08-05',
          }],
        },
        {
          id: 'retirement-batch-b', quantityTco2e: 220,
          registry: 'Gold Standard Registry', standard: 'Gold Standard',
          projectReference: 'GS-PROJECT-2023', retirementReference: 'REG-RET-2025-002',
          serialRange: 'GS-2023-LOT-B', vintage: '2023', retirementDate: '2025-08-20',
          beneficiary: 'Carbon Insight internal account', creditPriceJpyPerTco2e: 3000,
          priceSource: 'DEMO_COST_RATE_WORKBOOK_V1', verificationStatus: 'UNDER_REVIEW',
          evidenceRecords: [{
            id: 'evidence-credit-review-2025', name: 'Registry-retirement-220-FY2025.pdf',
            type: 'application/pdf', sizeBytes: 510000, reference: 'REG-RET-2025-002',
            evidenceType: 'RETIREMENT_CERTIFICATE', status: 'UNDER_REVIEW',
          }],
          audit: [{
            type: 'SUBMITTED_FOR_REVIEW', actor: 'Demo Admin', occurredAt: '2025-08-20',
          }],
        },
      ],
    }],
    fieldValues: {},
  },
];

export const DEMO_RECOMMENDATIONS = [
  {
    id: 'recommendation-green-landmark', rank: 1, solutionCode: 'green_electricity',
    scope: 'scope2', scopeGroup: 'scope2', category: 'electricity',
    fiscalYear: 2025, priority: 'high', readiness: 'ready', location: 'Landmark',
    boundaryId: 'Landmark', boundaryName: 'Landmark',
    boundaryAliases: ['office-landmark-01', 'Landmark Office'],
    hotspotId: 'scope2-landmark-fy2025', rankingBasis: 'annual_kwh',
    annualKwh: 92000, emissionFactorTco2ePerKwh: ELECTRICITY_FACTOR_TCO2E_PER_KWH,
    renewable: false,
    potentialImpact: 92000 * ELECTRICITY_FACTOR_TCO2E_PER_KWH,
    targetGapCoveragePercent: calculateTargetGapCoveragePercent({
      ...FY2025_SCOPE2_TARGET_GAP_INPUT,
      benefitTco2e: 92000 * ELECTRICITY_FACTOR_TCO2E_PER_KWH,
    }),
    translationKey: 'greenLandmark', plannedActionKey: 'greenLandmark',
    fieldValues: {
      office: 'Landmark', renewableKwh: '92000',
      emissionFactorTco2ePerKwh: String(ELECTRICITY_FACTOR_TCO2E_PER_KWH), period: 'FY2025',
      certificateReference: 'RE-LANDMARK-FY2025',
    },
  },
  {
    id: 'recommendation-green-okinawa-2', rank: 2, solutionCode: 'green_electricity',
    scope: 'scope2', scopeGroup: 'scope2', category: 'electricity',
    fiscalYear: 2025, priority: 'high', readiness: 'ready', location: 'Okinawa 2',
    boundaryId: 'Okinawa 2', boundaryName: 'Okinawa 2',
    boundaryAliases: ['Okinawa 1', 'office-okinawa-02'],
    hotspotId: 'scope2-okinawa-2-fy2025', rankingBasis: 'annual_kwh',
    annualKwh: 70000, emissionFactorTco2ePerKwh: 0.000677, renewable: false,
    potentialImpact: 70000 * 0.000677,
    targetGapCoveragePercent: calculateTargetGapCoveragePercent({
      ...FY2025_SCOPE2_TARGET_GAP_INPUT,
      benefitTco2e: 70000 * 0.000677,
    }),
    translationKey: 'greenOkinawa2', plannedActionKey: 'greenOkinawa2',
    fieldValues: {
      office: 'Okinawa 2', renewableKwh: '70000',
      emissionFactorTco2ePerKwh: '0.000677', period: 'FY2025',
      certificateReference: 'RE-OKINAWA-2-FY2025',
    },
  },
  {
    id: 'recommendation-business-travel', rank: 2, solutionCode: 'business_travel',
    fiscalYear: 2025, priority: 'medium', readiness: 'ready', location: 'All offices',
    potentialImpact: 96.2, translationKey: 'businessTravel', plannedActionKey: 'businessTravel',
    fieldValues: {
      office: 'All offices', activityValue: '96.2', period: 'FY2025',
      evidenceReference: 'TRAVEL-POLICY-FY2025',
    },
  },
  {
    id: 'recommendation-laptop-reuse', rank: 3, solutionCode: 'laptop_reuse',
    fiscalYear: 2025, priority: 'medium', readiness: 'needsData', location: 'All offices',
    potentialImpact: null, translationKey: 'laptopReuse', plannedActionKey: 'laptopReuse',
    fieldValues: {
      reusedQuantity: '', weightKg: '', disposalBaseline: '', period: 'FY2025',
      evidenceReference: '',
    },
  },
  {
    id: 'recommendation-fleet-ev', rank: 4, solutionCode: 'fleet_ev',
    fiscalYear: 2025, priority: 'medium', readiness: 'ready', location: 'Tokyo fleet',
    potentialImpact: 12.4, translationKey: 'fleetEv', plannedActionKey: 'fleetEv',
    fieldValues: {
      office: 'Tokyo fleet', activityValue: '29450', period: 'FY2025',
      evidenceReference: 'FLEET-ASSESSMENT-FY2025',
    },
  },
  {
    id: 'recommendation-hvac', rank: 5, solutionCode: 'hvac_efficiency',
    fiscalYear: 2025, priority: 'medium', readiness: 'needsData', location: 'Osaka Office',
    boundaryId: 'office-osaka-01', boundaryName: 'Osaka Office',
    potentialImpact: null, translationKey: 'hvac', plannedActionKey: 'hvac',
    fieldValues: {
      office: 'Osaka Office', activityValue: '', period: 'FY2025', evidenceReference: '',
    },
  },
  {
    id: 'recommendation-dhl', rank: 6, solutionCode: 'dhl_gogreen',
    fiscalYear: 2025, priority: 'low', readiness: 'needsData', location: 'Company-wide shipping',
    potentialImpact: null, translationKey: 'dhl', plannedActionKey: 'dhl',
    fieldValues: {
      allocatedTco2e: '', period: 'FY2025', certificateReference: '',
    },
  },
  {
    id: 'recommendation-device-life', rank: 7, solutionCode: 'device_life_extension',
    fiscalYear: 2025, priority: 'medium', readiness: 'ready', location: 'All offices',
    potentialImpact: 18.5, translationKey: 'deviceLife', plannedActionKey: 'deviceLife',
    fieldValues: {
      reusedQuantity: '240', period: 'FY2025', evidenceReference: 'ASSET-POLICY-FY2025',
    },
  },
  {
    id: 'recommendation-virtual-meetings', rank: 8, solutionCode: 'virtual_meeting_policy',
    fiscalYear: 2025, priority: 'low', readiness: 'ready', location: 'All offices',
    potentialImpact: 42.8, translationKey: 'virtualMeetings', plannedActionKey: 'virtualMeetings',
    fieldValues: {
      office: 'All offices', activityValue: '42.8', period: 'FY2025',
      evidenceReference: 'TRAVEL-POLICY-FY2025',
    },
  },
  {
    id: 'recommendation-fleet-ev-2024', rank: 1, solutionCode: 'fleet_ev',
    fiscalYear: 2024, priority: 'medium', readiness: 'ready', location: 'Tokyo fleet',
    potentialImpact: 12.4, translationKey: 'fleetEv', plannedActionKey: 'fleetEv',
    fieldValues: {
      office: 'Tokyo fleet', activityValue: '29450', period: 'FY2024',
      evidenceReference: 'FLEET-ASSESSMENT-FY2024',
    },
  },
  {
    id: 'recommendation-hvac-2024', rank: 2, solutionCode: 'hvac_efficiency',
    fiscalYear: 2024, priority: 'medium', readiness: 'needsData', location: 'Osaka Office',
    boundaryId: 'office-osaka-01', boundaryName: 'Osaka Office',
    potentialImpact: null, translationKey: 'hvac', plannedActionKey: 'hvac',
    fieldValues: {
      office: 'Osaka Office', activityValue: '', period: 'FY2024', evidenceReference: '',
    },
  },
  {
    id: 'recommendation-dhl-2024', rank: 3, solutionCode: 'dhl_gogreen',
    fiscalYear: 2024, priority: 'low', readiness: 'needsData', location: 'Company-wide shipping',
    potentialImpact: null, translationKey: 'dhl', plannedActionKey: 'dhl',
    fieldValues: {
      allocatedTco2e: '', period: 'FY2024', certificateReference: '',
    },
  },
];

const DEFAULT_ACTION_OWNER = Object.freeze({ id: 'demo-admin', name: 'Demo Admin' });
export const MAX_EVIDENCE_FILE_SIZE_BYTES = 25 * 1024 * 1024;
export const MAX_EVIDENCE_FILE_COUNT = 10;
export const ACCEPTED_EVIDENCE_FILE_EXTENSIONS = Object.freeze([
  'pdf', 'jpg', 'jpeg', 'png', 'xlsx', 'csv', 'docx',
]);
const MASTER_VERIFICATION_CRITICAL_FIELDS = Object.freeze([
  'solutionCode', 'actionType', 'accountingClassification', 'scope', 'scopeGroup',
  'category', 'subcategory', 'boundary', 'boundaryId', 'boundaryName',
]);
const ANNUAL_VERIFICATION_CRITICAL_FIELDS = Object.freeze([
  'impactRecords', 'evidenceRecords',
]);
function numberOrNull(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function nonNegativeNumberOrNull(value, fieldName) {
  const numericValue = numberOrNull(value);
  if (numericValue === null) return null;
  if (numericValue < 0) {
    throw new RangeError(fieldName + ' must be zero or greater.');
  }
  return numericValue;
}

/** Convert an activity change or target impact using one explicit emission factor. */
export function calculateActivityImpact(calculationInput = {}) {
  const inputMode = calculationInput.inputMode || ACTIVITY_INPUT_MODES.ACTIVITY_CHANGE;
  if (!Object.values(ACTIVITY_INPUT_MODES).includes(inputMode)) {
    throw new RangeError('Activity input mode is not supported.');
  }
  const emissionFactor = nonNegativeNumberOrNull(
    calculationInput.emissionFactorTco2ePerUnit,
    'Emission factor',
  );
  if (emissionFactor === null || emissionFactor === 0) {
    return {
      inputMode,
      nativeUnit: calculationInput.nativeUnit || null,
      activityChange: null,
      estimatedImpactTco2e: null,
      spendChangeEquivalentJpy: null,
      readiness: 'EMISSION_FACTOR_REQUIRED',
    };
  }
  const targetImpact = nonNegativeNumberOrNull(
    calculationInput.targetImpactTco2e,
    'Target impact',
  );
  const enteredActivity = nonNegativeNumberOrNull(
    calculationInput.activityChange,
    'Activity change',
  );
  const activityChange = inputMode === ACTIVITY_INPUT_MODES.TARGET_TCO2E
    ? (targetImpact === null ? null : targetImpact / emissionFactor)
    : enteredActivity;
  const estimatedImpactTco2e = inputMode === ACTIVITY_INPUT_MODES.TARGET_TCO2E
    ? targetImpact
    : (activityChange === null ? null : activityChange * emissionFactor);
  const spendChangeEquivalentJpy = calculationInput.isSpendEquivalent
    && activityChange !== null
    ? activityChange * (calculationInput.nativeUnit === 'JPY' ? 1 : 1000000)
    : null;
  return {
    inputMode,
    nativeUnit: calculationInput.nativeUnit || null,
    activityChange,
    estimatedImpactTco2e,
    spendChangeEquivalentJpy,
    readiness: estimatedImpactTco2e === null ? 'ACTIVITY_DATA_REQUIRED' : 'AVAILABLE',
  };
}

/** Derive comparable cost metrics without treating spend-equivalent as project cost. */
export function calculateFinancialFeasibility(financialInput = {}) {
  const activityChange = nonNegativeNumberOrNull(
    financialInput.activityChange,
    'Activity change',
  );
  const estimatedImpact = nonNegativeNumberOrNull(
    financialInput.estimatedImpactTco2e,
    'Estimated impact',
  );
  const unitPriceJpy = nonNegativeNumberOrNull(financialInput.unitPriceJpy, 'Unit price');
  const implementationCostJpy = nonNegativeNumberOrNull(
    financialInput.implementationCostJpy,
    'Implementation cost',
  );
  const annualSavingsJpy = nonNegativeNumberOrNull(
    financialInput.annualSavingsJpy,
    'Annual savings',
  );
  const activityCostEffectJpy = activityChange !== null && unitPriceJpy !== null
    ? activityChange * unitPriceJpy
    : null;
  const costPerTco2e = implementationCostJpy !== null && estimatedImpact > 0
    ? implementationCostJpy / estimatedImpact
    : null;
  const paybackYears = implementationCostJpy !== null && annualSavingsJpy > 0
    ? implementationCostJpy / annualSavingsJpy
    : null;
  const hasFinancialInput = [
    unitPriceJpy, implementationCostJpy, annualSavingsJpy,
    financialInput.spendChangeEquivalentJpy,
  ].some(value => numberOrNull(value) !== null);
  return {
    readiness: hasFinancialInput
      ? FINANCIAL_READINESS.AVAILABLE
      : FINANCIAL_READINESS.COST_DATA_REQUIRED,
    unitPriceJpy,
    activityCostEffectJpy,
    implementationCostJpy,
    annualSavingsJpy,
    spendChangeEquivalentJpy: numberOrNull(financialInput.spendChangeEquivalentJpy),
    costPerTco2e,
    paybackYears,
  };
}

/** Calculate activity and finance projections from the same immutable input. */
export function calculateActionFeasibility(calculationInput = {}) {
  const activity = calculateActivityImpact(calculationInput);
  const financial = calculateFinancialFeasibility({
    ...calculationInput,
    activityChange: activity.activityChange,
    estimatedImpactTco2e: activity.estimatedImpactTco2e,
    spendChangeEquivalentJpy: activity.spendChangeEquivalentJpy,
  });
  return { activity, financial };
}

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.values(value).forEach(deepFreeze);
  return Object.freeze(value);
}

function valuesMatch(left, right) {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

/** Normalize canonical and legacy UI status values to the workflow contract. */
export function canonicalWorkflowStatus(status) {
  return canonicalDeliveryStatus(status, null);
}

/** Normalize the canonical action type while reading former action-kind records. */
export function canonicalActionType(actionOrType) {
  const explicitType = actionOrType && typeof actionOrType === 'object'
    ? (actionOrType.actionType || actionOrType.actionKind)
    : actionOrType;
  const normalizedType = String(explicitType || '').replace(/[^a-z]/gi, '').toLowerCase();
  if (normalizedType) {
    return ['retiredcarboncredit', 'retiredcredit', 'carboncreditretirement']
      .includes(normalizedType)
      ? ACTION_TYPES.CARBON_CREDIT_RETIREMENT
      : ACTION_TYPES.CLIMATE_ACTION;
  }
  if (!actionOrType || typeof actionOrType !== 'object') {
    return ACTION_TYPES.CLIMATE_ACTION;
  }
  const legacyType = String(
    actionOrType.accountingClassification
      || (actionOrType.solutionCode === 'retired_offset' ? 'retiredCarbonCredit' : ''),
  ).replace(/[^a-z]/gi, '').toLowerCase();
  return legacyType === 'retiredcarboncredit'
    ? ACTION_TYPES.CARBON_CREDIT_RETIREMENT
    : ACTION_TYPES.CLIMATE_ACTION;
}

/** Compatibility alias for callers migrating from actionKind. */
export const canonicalActionKind = canonicalActionType;

/** Derive the accounting class used by selectors from the canonical action kind. */
export function accountingClassificationForAction(action = {}) {
  if (canonicalActionType(action) === ACTION_TYPES.CARBON_CREDIT_RETIREMENT) {
    return ACCOUNTING_CLASSIFICATIONS.RETIRED_CARBON_CREDIT;
  }
  const explicitClassification = action.accountingClassification;
  if (Object.values(ACCOUNTING_CLASSIFICATIONS).includes(explicitClassification)
    && explicitClassification !== ACCOUNTING_CLASSIFICATIONS.RETIRED_CARBON_CREDIT) {
    return explicitClassification;
  }
  if (action.scopeGroup && action.scopeGroup !== RECOMMENDATION_SCOPE_GROUPS.OTHER) {
    return ACCOUNTING_CLASSIFICATIONS.SCOPE_LINKED_REDUCTION;
  }
  return solutionMetadata(action.solutionCode).accountingClassification
    === ACCOUNTING_CLASSIFICATIONS.RETIRED_CARBON_CREDIT
    ? ACCOUNTING_CLASSIFICATIONS.CLIMATE_CONTRIBUTION
    : solutionMetadata(action.solutionCode).accountingClassification;
}

/** Normalize the independent verification state used by portfolio accounting. */
export function canonicalVerificationStatus(status) {
  if (Object.values(CLIMATE_ACTION_VERIFICATION_STATUS).includes(status)) return status;
  return CLIMATE_ACTION_VERIFICATION_STATUS.NOT_SUBMITTED;
}

/** Return the action-level review decision independently from annual verification. */
export function actionReviewStatus(action) {
  const workflowStatus = canonicalWorkflowStatus(
    action.actionStatus || action.workflowStatus || action.status,
  );
  if ([CLIMATE_ACTION_WORKFLOW_STATUS.IN_PROGRESS, CLIMATE_ACTION_WORKFLOW_STATUS.COMPLETED]
    .includes(workflowStatus)) return CLIMATE_ACTION_REVIEW_STATUS.APPROVED;
  if (workflowStatus === CLIMATE_ACTION_WORKFLOW_STATUS.NOT_APPROVED) {
    return CLIMATE_ACTION_REVIEW_STATUS.NOT_APPROVED;
  }
  if (Object.values(CLIMATE_ACTION_REVIEW_STATUS).includes(action.reviewStatus)) {
    return action.reviewStatus;
  }
  // Read-only compatibility for records created before actionStatus became canonical.
  if (action.isApprovedForCompensation === true) return CLIMATE_ACTION_REVIEW_STATUS.APPROVED;
  return CLIMATE_ACTION_REVIEW_STATUS.PENDING_REVIEW;
}

/** Return whether an action has been approved for Climate Action impact reporting. */
export function actionIsApproved(action) {
  return actionReviewStatus(action) === CLIMATE_ACTION_REVIEW_STATUS.APPROVED;
}

/** Validate the legal workflow states for a newly entered manual action. */
export function validateManualActionInitialStatus(status) {
  const workflowStatus = canonicalWorkflowStatus(status);
  return {
    valid: MANUAL_INITIAL_STATUSES.has(workflowStatus),
    workflowStatus,
  };
}

/** Create one canonical impact record without inferring a stronger accounting type. */
export function createImpactRecord({
  id, stage, type, fiscalYear, amountTco2e, recordedAt, source = 'demo',
  solutionCode, accountingCategory,
}) {
  const impactStage = stage || type;
  if (!Object.values(IMPACT_RECORD_TYPES).includes(impactStage)) {
    throw new RangeError('Impact record type must be ESTIMATED, REPORTED or VERIFIED.');
  }
  const year = Number(fiscalYear);
  const amount = numberOrNull(amountTco2e);
  if (!Number.isInteger(year) || amount === null || amount < 0) {
    throw new RangeError('Impact record requires a fiscal year and a non-negative tCO2e amount.');
  }
  return {
    id: id || `impact-${impactStage.toLowerCase()}-${year}`,
    stage: impactStage,
    type: impactStage,
    fiscalYear: year,
    amountTco2e: amount,
    recordedAt: recordedAt || null,
    source,
    ...(solutionCode ? { solutionCode } : {}),
    ...(accountingCategory ? { accountingCategory } : {}),
  };
}

function stageForImpactRecord(impactRecord) {
  return impactRecord.stage || impactRecord.type;
}

function valueForImpactRecord(impactRecord) {
  return numberOrNull(
    impactRecord?.amountTco2e
      ?? impactRecord?.valueTco2e
      ?? impactRecord?.value
      ?? impactRecord?.tco2e,
  );
}

/** Sum one accounting stage for one FY without mixing estimate, report and verification. */
export function impactAmountForStageAndYear(impactRecords = [], fiscalYear, stage) {
  const amounts = impactRecords
    .filter(record => stageForImpactRecord(record) === stage)
    .filter(record => Number(record.fiscalYear ?? record.year) === Number(fiscalYear))
    .map(valueForImpactRecord)
    .filter(Number.isFinite);
  return amounts.length > 0
    ? amounts.reduce((total, amount) => total + amount, 0)
    : null;
}

/** Select the strongest available accounting stage for an FY and keep its amount typed. */
export function selectedImpactForYear(impactRecords = [], fiscalYear) {
  const stage = IMPACT_STAGE_ORDER.find(candidate => (
    impactAmountForStageAndYear(impactRecords, fiscalYear, candidate) !== null
  ));
  if (!stage) return null;
  return {
    stage,
    amountTco2e: impactAmountForStageAndYear(impactRecords, fiscalYear, stage),
    unit: impactRecords.find(record => (
      stageForImpactRecord(record) === stage
      && Number(record.fiscalYear ?? record.year) === Number(fiscalYear)
    ))?.unit || 'tCO2e',
  };
}

function legacyAnnualResultForYear(action, fiscalYear) {
  const selectedYear = Number(fiscalYear);
  const reportedAmount = impactAmountForStageAndYear(
    action.impactRecords || [],
    selectedYear,
    IMPACT_RECORD_TYPES.REPORTED,
  );
  const verifiedAmount = impactAmountForStageAndYear(
    action.impactRecords || [],
    selectedYear,
    IMPACT_RECORD_TYPES.VERIFIED,
  );
  const estimatedAmount = impactAmountForStageAndYear(
    action.impactRecords || [],
    selectedYear,
    IMPACT_RECORD_TYPES.ESTIMATED,
  );
  const yearlyValue = numberOrNull(action.submittedImpactTco2eByYear?.[selectedYear]);
  const submittedAmount = reportedAmount ?? verifiedAmount ?? yearlyValue
    ?? (Number(action.fiscalYear) === selectedYear
      ? numberOrNull(action.submittedImpactTco2e)
      : null);
  if ([estimatedAmount, submittedAmount, verifiedAmount].every(value => value === null)) return null;
  const status = verifiedAmount !== null
    ? CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED
    : canonicalVerificationStatus(action.verificationStatus);
  return {
    fiscalYear: selectedYear,
    estimatedImpactTco2e: estimatedAmount,
    reportedImpactTco2e: submittedAmount,
    verifiedImpactTco2e: verifiedAmount,
    verificationStatus: status,
    evidenceRecords: (action.evidenceRecords || []).filter(record => (
      record.fiscalYear === undefined || Number(record.fiscalYear) === selectedYear
    )),
    annualCostJpy: numberOrNull(action.financialEstimate?.annualCostJpy),
    annualSavingsJpy: numberOrNull(action.financialEstimate?.annualSavingsJpy),
  };
}

function normalizeAnnualResult(annualResult) {
  const verifiedImpactTco2e = numberOrNull(annualResult.verifiedImpactTco2e);
  const reportedImpactTco2e = numberOrNull(annualResult.reportedImpactTco2e);
  let verificationStatus = canonicalVerificationStatus(annualResult.verificationStatus);
  if (verifiedImpactTco2e !== null) verificationStatus = CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED;
  if (verificationStatus === CLIMATE_ACTION_VERIFICATION_STATUS.NOT_YET_VERIFIED) {
    verificationStatus = reportedImpactTco2e === null
      ? CLIMATE_ACTION_VERIFICATION_STATUS.NOT_SUBMITTED
      : CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW;
  }
  return {
    ...annualResult,
    fiscalYear: Number(annualResult.fiscalYear),
    estimatedImpactTco2e: numberOrNull(annualResult.estimatedImpactTco2e),
    reportedImpactTco2e,
    verifiedImpactTco2e,
    verificationStatus,
    evidenceRecords: (annualResult.evidenceRecords || []).map(record => ({ ...record })),
    retirementBatches: (annualResult.retirementBatches || []).map((batch, index) => (
      normalizeRetirementBatches({ retirementBatches: [batch] })[0]
        || { ...batch, id: batch.id || `retirement-batch-${index + 1}` }
    )),
    annualCostJpy: numberOrNull(annualResult.annualCostJpy),
    annualSavingsJpy: numberOrNull(annualResult.annualSavingsJpy),
  };
}

function annualResultWithDerivedCreditAmounts(action, annualResult) {
  const normalized = normalizeAnnualResult(annualResult);
  if (!isRetiredCarbonCreditAction(action)) return normalized;
  const retirementBatches = normalizeRetirementBatches(normalized, action);
  // The simplified FE flow stores one FY result plus evidence for either action type.
  // Keep the batch-aware path for legacy demo records, but do not erase a simple result.
  if (retirementBatches.length === 0) return normalized;
  const summary = retirementBatchSummary(retirementBatches);
  const submittedTco2e = summary.verifiedTco2e
    + summary.underReviewTco2e
    + summary.rejectedTco2e;
  const verificationStatus = summary.underReviewTco2e > 0
    ? CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW
    : summary.rejectedTco2e > 0
      ? CLIMATE_ACTION_VERIFICATION_STATUS.REJECTED
      : summary.verifiedTco2e > 0
        ? CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED
        : CLIMATE_ACTION_VERIFICATION_STATUS.NOT_SUBMITTED;
  return {
    ...normalized,
    retirementBatches,
    retirementBatchSummary: summary,
    evidenceRecords: normalized.evidenceRecords.length > 0
      ? normalized.evidenceRecords
      : retirementBatches.flatMap(batch => batch.evidenceRecords || []),
    reportedImpactTco2e: submittedTco2e > 0 ? submittedTco2e : null,
    verifiedImpactTco2e: summary.verifiedTco2e > 0 ? summary.verifiedTco2e : null,
    verificationStatus,
  };
}

/** Return the independent impact, evidence and verification record for one FY. */
export function annualResultForYear(action, fiscalYear) {
  const selectedYear = Number(fiscalYear);
  const actionFiscalYear = Number(action.fiscalYear);
  if (Number.isInteger(actionFiscalYear) && actionFiscalYear !== selectedYear) return null;
  const annualResult = (action.annualResults || []).find(
    candidate => Number(candidate.fiscalYear) === selectedYear,
  );
  if (annualResult) return annualResultWithDerivedCreditAmounts(action, annualResult);
  const legacyResult = legacyAnnualResultForYear(action, selectedYear);
  return legacyResult ? annualResultWithDerivedCreditAmounts(action, legacyResult) : null;
}

/** Return the action impact submitted for one FY without promoting an AI estimate. */
export function submittedImpactForYear(action, fiscalYear) {
  const annualResult = annualResultForYear(action, fiscalYear);
  if (!annualResult) return null;
  return annualResult.reportedImpactTco2e ?? annualResult.verifiedImpactTco2e;
}

/** Present one action's recorded FY impact, preferring the reported value. */
export function annualImpactPresentation(action, fiscalYear) {
  const result = annualResultForYear(action, fiscalYear);
  if (!result) return null;
  const verified = result.verifiedImpactTco2e ?? result.verified_impact_tco2e;
  const reported = result.reportedImpactTco2e ?? result.reported_impact_tco2e;
  const recorded = reported ?? verified;
  if (recorded !== null && recorded !== undefined) return { stage: 'recorded', value: Number(recorded) };
  return null;
}

/** Return whether an action belongs to exactly one selected FY. */
export function actionAppliesToFiscalYear(action, fiscalYear) {
  const selectedYear = Number(fiscalYear);
  const actionFiscalYear = Number(action.fiscalYear);
  if (Number.isInteger(actionFiscalYear)) return actionFiscalYear === selectedYear;
  const legacyFiscalYears = [...new Set([
    ...(action.annualResults || []).map(result => Number(result.fiscalYear)),
    ...(action.impactRecords || []).map(record => Number(record.fiscalYear ?? record.year)),
  ])].filter(Number.isInteger);
  return legacyFiscalYears.length === 1 && legacyFiscalYears[0] === selectedYear;
}

function impactRowsForYear(actions, fiscalYear) {
  const selectedYear = Number(fiscalYear);
  return actions.flatMap((action) => {
    if (!actionAppliesToFiscalYear(action, selectedYear)) return [];
    if (!actionIsApproved(action)) return [];
    const annualResult = annualResultForYear(action, selectedYear);
    if (!annualResult) return [];
    const retiredCredit = isRetiredCarbonCreditAction(action);
    if (retiredCredit) {
      const retirementBatches = normalizeRetirementBatches(annualResult, action);
      if (retirementBatches.length > 0) return retirementBatches.flatMap((batch) => {
        if (!validateRetirementBatch(batch, selectedYear, retirementBatches).valid) return [];
        const isVerified = batch.verificationStatus === RETIREMENT_BATCH_STATUS.VERIFIED;
        const isUnderReview = batch.verificationStatus
          === RETIREMENT_BATCH_STATUS.UNDER_REVIEW;
        if ((!isVerified && !isUnderReview) || !(batch.quantityTco2e > 0)) return [];
        return [{
          action,
          annualResult,
          retirementBatch: batch,
          amountTco2e: batch.quantityTco2e,
          isVerified,
          isRetiredCredit: true,
        }];
      });
    }
    const isVerified = annualResult.verificationStatus
      === CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED;
    const isUnderReview = annualResult.verificationStatus
      === CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW;
    if (!isVerified && !isUnderReview) return [];
    const amountTco2e = isVerified
      ? annualResult.verifiedImpactTco2e
      : annualResult.reportedImpactTco2e;
    if (amountTco2e === null) return [];
    return [{
      action,
      annualResult,
      amountTco2e,
      isVerified,
      isRetiredCredit: retiredCredit,
    }];
  });
}

const amountOfImpactRows = rows => rows.reduce((total, row) => total + row.amountTco2e, 0);

/** Summarize approved action impact independently from gross-emissions accounting. */
export function climateActionImpactForYear(actions = [], fiscalYear) {
  const included = impactRowsForYear(actions, fiscalYear);
  const verifiedRows = included.filter(row => row.isVerified);
  const underReviewRows = included.filter(row => !row.isVerified);
  const retiredVerifiedRows = verifiedRows.filter(row => row.isRetiredCredit);
  const otherVerifiedRows = verifiedRows.filter(row => !row.isRetiredCredit);
  const retiredUnderReviewRows = underReviewRows.filter(row => row.isRetiredCredit);
  const otherUnderReviewRows = underReviewRows.filter(row => !row.isRetiredCredit);
  const amountOf = rows => rows.reduce((total, row) => total + row.amountTco2e, 0);
  const breakdownMap = included.reduce((groups, row) => {
    const type = accountingClassificationForAction(row.action);
    groups[type] = (groups[type] || 0) + row.amountTco2e;
    return groups;
  }, {});
  const verifiedImpactTco2e = amountOf(verifiedRows);
  const underReviewImpactTco2e = amountOf(underReviewRows);
  const verifiedRetiredCreditsTco2e = amountOf(retiredVerifiedRows);
  const creditsUnderReviewTco2e = amountOf(retiredUnderReviewRows);
  const otherVerifiedClimateActionImpactTco2e = amountOf(otherVerifiedRows);
  const otherClimateActionImpactUnderReviewTco2e = amountOf(otherUnderReviewRows);
  const totalImpactTco2e = verifiedImpactTco2e + underReviewImpactTco2e;
  const verifiedInBoundaryReductionsTco2e = amountOf(verifiedRows.filter(row => (
    accountingClassificationForAction(row.action)
      === ACCOUNTING_CLASSIFICATIONS.SCOPE_LINKED_REDUCTION
  )));
  const verificationCoveragePercent = totalImpactTco2e > 0
    ? Math.round((verifiedImpactTco2e / totalImpactTco2e) * 1000) / 10
    : 0;

  return {
    fiscalYear: Number(fiscalYear),
    totalImpactTco2e,
    totalVerifiedImpactTco2e: verifiedImpactTco2e,
    verifiedRetiredCreditsTco2e,
    creditsUnderReviewTco2e,
    otherVerifiedClimateActionImpactTco2e,
    otherClimateActionImpactUnderReviewTco2e,
    eligibleVerifiedImpactTco2e: verifiedRetiredCreditsTco2e,
    nonEligibleVerifiedImpactTco2e: otherVerifiedClimateActionImpactTco2e,
    pendingVerificationTco2e: underReviewImpactTco2e,
    verifiedImpactTco2e,
    underReviewImpactTco2e,
    verifiedInBoundaryReductionsTco2e,
    verificationCoveragePercent,
    verifiedActionCount: verifiedRows.length,
    underReviewActionCount: underReviewRows.length,
    // Compatibility keys used by the existing UI during the incremental refactor.
    totalSubmittedTco2e: totalImpactTco2e,
    verifiedTco2e: verifiedImpactTco2e,
    pendingTco2e: underReviewImpactTco2e,
    pendingActionCount: underReviewRows.length,
    breakdown: Object.entries(breakdownMap).map(([type, amountTco2e]) => ({ type, amountTco2e })),
  };
}

/** Sum only verified reductions already reflected inside the Scope 1-3 inventory boundary. */
export function verifiedInBoundaryReductionsForYear(actions = [], fiscalYear) {
  return amountOfImpactRows(impactRowsForYear(actions, fiscalYear).filter(row => (
    row.isVerified
    && accountingClassificationForAction(row.action)
      === ACCOUNTING_CLASSIFICATIONS.SCOPE_LINKED_REDUCTION
  )));
}

/** Compatibility alias for the former portfolio selector. */
export function compensationPortfolioForYear(actions = [], fiscalYear) {
  return climateActionImpactForYear(actions, fiscalYear);
}

function isRetiredCarbonCreditAction(action) {
  return canonicalActionType(action) === ACTION_TYPES.CARBON_CREDIT_RETIREMENT;
}

/** Derive internal Carbon Credit Balance readiness without a generic approval boolean. */
export function carbonCreditCompensationEligibilityForYear(action, fiscalYear) {
  const annualResult = annualResultForYear(action, fiscalYear);
  const actionApproved = actionIsApproved(action);
  const retiredCarbonCredit = isRetiredCarbonCreditAction(action);
  const batches = retiredCarbonCredit && annualResult
    ? normalizeRetirementBatches(annualResult, action)
    : [];
  const usesLegacyBatches = batches.length > 0;
  const relevantBatches = batches.filter(batch => [
    RETIREMENT_BATCH_STATUS.UNDER_REVIEW,
    RETIREMENT_BATCH_STATUS.VERIFIED,
  ].includes(batch.verificationStatus));
  const validations = relevantBatches.map(batch => validateRetirementBatch(
    batch, fiscalYear, batches,
  ));
  const simpleVerificationStatus = canonicalVerificationStatus(
    annualResult?.verificationStatus,
  );
  const retirementDetailsComplete = usesLegacyBatches
    ? relevantBatches.length > 0 && validations.every(validation => validation.valid)
    : numberOrNull(annualResult?.reportedImpactTco2e) > 0;
  const retirementEvidenceValid = usesLegacyBatches
    ? relevantBatches.length > 0
      && relevantBatches.every(batch => batch.evidenceRecords.length > 0)
    : (annualResult?.evidenceRecords || []).length > 0;
  const batchSummary = retirementBatchSummary(batches);
  const verifiedTco2e = usesLegacyBatches
    ? batchSummary.verifiedTco2e
    : simpleVerificationStatus === CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED
      ? numberOrNull(annualResult?.verifiedImpactTco2e
        ?? annualResult?.reportedImpactTco2e) || 0
      : 0;
  const underReviewTco2e = usesLegacyBatches
    ? batchSummary.underReviewTco2e
    : simpleVerificationStatus === CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW
      ? numberOrNull(annualResult?.reportedImpactTco2e) || 0
      : 0;
  const verificationStatus = usesLegacyBatches
    ? underReviewTco2e > 0
      ? CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW
      : verifiedTco2e > 0
        ? CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED
        : CLIMATE_ACTION_VERIFICATION_STATUS.NOT_SUBMITTED
    : simpleVerificationStatus;
  const statusEligible = usesLegacyBatches
    ? relevantBatches.length > 0
    : [
      CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW,
      CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED,
    ].includes(simpleVerificationStatus);
  const reasonCodes = [
    ...(!actionApproved ? ['ACTION_NOT_APPROVED'] : []),
    ...(!retiredCarbonCredit ? ['NOT_RETIRED_CARBON_CREDIT'] : []),
    ...(!statusEligible ? ['FY_RESULT_NOT_REVIEWABLE'] : []),
    ...(!retirementDetailsComplete ? ['RETIREMENT_DETAILS_INCOMPLETE'] : []),
    ...(!retirementEvidenceValid ? ['RETIREMENT_EVIDENCE_INVALID'] : []),
  ];
  const validForBalance = reasonCodes.length === 0;
  const creditBalanceStatus = !retiredCarbonCredit
    ? 'NOT_APPLICABLE'
    : !actionApproved || relevantBatches.length === 0
      ? 'NOT_READY'
      : underReviewTco2e > 0
        ? 'UNDER_REVIEW'
        : verifiedTco2e > 0 && validForBalance
          ? 'APPROVED'
          : 'REJECTED';
  return {
    // Compatibility key for existing filters: reviewable credit, not a persisted authority.
    eligible: validForBalance,
    actionApproved,
    retiredCarbonCredit,
    requiredDataComplete: retirementDetailsComplete,
    statusEligible,
    retirementDetailsComplete,
    retirementEvidenceValid,
    verificationStatus,
    annualResult,
    creditBalanceStatus,
    verifiedTco2e,
    underReviewTco2e,
    reasonCodes,
  };
}

/** Calculate real carbon-credit compensation separately from all other action impact. */
export function carbonCreditCompensationForYear(actions = [], fiscalYear, grossEmissions = 0) {
  const reconciledRows = impactRowsForYear(actions, fiscalYear);
  const verifiedRows = reconciledRows.filter(row => row.isRetiredCredit && row.isVerified);
  const underReviewRows = reconciledRows.filter(row => row.isRetiredCredit && !row.isVerified);
  const verifiedNonEligibleRows = reconciledRows
    .filter(row => !row.isRetiredCredit && row.isVerified);
  const underReviewNonEligibleRows = reconciledRows
    .filter(row => !row.isRetiredCredit && !row.isVerified);
  const grossEmissionsTco2e = Math.max(numberOrNull(grossEmissions) || 0, 0);
  const verifiedRetiredCreditsTco2e = amountOfImpactRows(verifiedRows);
  const creditsUnderReviewTco2e = amountOfImpactRows(underReviewRows);
  const verifiedNonEligibleImpactTco2e = amountOfImpactRows(verifiedNonEligibleRows);
  const underReviewNonEligibleImpactTco2e = amountOfImpactRows(
    underReviewNonEligibleRows,
  );
  const remaining = Math.max(grossEmissionsTco2e - verifiedRetiredCreditsTco2e, 0);
  const surplusVerifiedCreditsTco2e = Math.max(
    verifiedRetiredCreditsTco2e - grossEmissionsTco2e, 0,
  );
  return {
    fiscalYear: Number(fiscalYear),
    grossEmissionsTco2e,
    totalVerifiedImpactTco2e:
      verifiedRetiredCreditsTco2e + verifiedNonEligibleImpactTco2e,
    eligibleVerifiedImpactTco2e: verifiedRetiredCreditsTco2e,
    nonEligibleVerifiedImpactTco2e: verifiedNonEligibleImpactTco2e,
    pendingVerificationTco2e:
      creditsUnderReviewTco2e + underReviewNonEligibleImpactTco2e,
    verifiedRetiredCreditsTco2e,
    creditsUnderReviewTco2e,
    verifiedNonEligibleImpactTco2e,
    underReviewNonEligibleImpactTco2e,
    remainingAfterCompensationTco2e: remaining,
    emissionsAfterVerifiedRetirementsTco2e: remaining,
    surplusVerifiedCreditsTco2e,
    potentialRemainingTco2e: Math.max(
      grossEmissionsTco2e - verifiedRetiredCreditsTco2e - creditsUnderReviewTco2e,
      0,
    ),
    verifiedCoveragePercent: grossEmissionsTco2e > 0
      ? (verifiedRetiredCreditsTco2e / grossEmissionsTco2e) * 100
      : 0,
    plannedCoveragePercent: grossEmissionsTco2e > 0
      ? ((verifiedRetiredCreditsTco2e + creditsUnderReviewTco2e) / grossEmissionsTco2e) * 100
      : 0,
    verifiedActionCount: verifiedRows.length,
    underReviewActionCount: underReviewRows.length,
    verifiedNonEligibleActionCount: verifiedNonEligibleRows.length,
    underReviewNonEligibleActionCount: underReviewNonEligibleRows.length,
  };
}

/**
 * Provide one accounting-safe presentation selector.
 * In-boundary reductions are disclosed separately and are never subtracted from Gross again.
 */
export function verifiedImpactCreditReconciliationForYear({
  actions = [],
  fiscalYear,
  grossEmissions = 0,
  targetEmissions = null,
} = {}) {
  const impact = climateActionImpactForYear(actions, fiscalYear);
  const compensation = carbonCreditCompensationForYear(
    actions,
    fiscalYear,
    grossEmissions,
  );
  const normalizedTarget = numberOrNull(targetEmissions);
  const gapToTargetTco2e = normalizedTarget === null
    ? null
    : compensation.grossEmissionsTco2e - normalizedTarget;
  const targetStatus = gapToTargetTco2e === null
    ? 'NO_TARGET'
    : (Math.abs(gapToTargetTco2e) < Number.EPSILON
      ? 'ON_TARGET'
      : (gapToTargetTco2e > 0 ? 'ABOVE_TARGET' : 'AHEAD_OF_TARGET'));
  return {
    fiscalYear: Number(fiscalYear),
    grossEmissionsTco2e: compensation.grossEmissionsTco2e,
    totalVerifiedImpactTco2e: impact.totalVerifiedImpactTco2e,
    eligibleVerifiedImpactTco2e: impact.eligibleVerifiedImpactTco2e,
    nonEligibleVerifiedImpactTco2e: impact.nonEligibleVerifiedImpactTco2e,
    pendingVerificationTco2e: impact.pendingVerificationTco2e,
    // Compatibility aliases retained for existing dashboard and report consumers.
    verifiedActionImpactTco2e: impact.verifiedImpactTco2e,
    verifiedInBoundaryReductionsTco2e: impact.verifiedInBoundaryReductionsTco2e,
    eligibleRetiredCreditsTco2e: compensation.verifiedRetiredCreditsTco2e,
    remainingAfterEligibleCreditsTco2e:
      compensation.remainingAfterCompensationTco2e,
    verificationCoveragePercent: impact.verificationCoveragePercent,
    gapToTargetTco2e,
    targetStatus,
  };
}

/** Replace only the selected FY's reported record; blank input means no reported impact. */
export function upsertReportedImpactRecord({ impactRecords = [], fiscalYear, reportedImpact }) {
  const retainedRecords = impactRecords.filter(record => !(
    stageForImpactRecord(record) === IMPACT_RECORD_TYPES.REPORTED
    && Number(record.fiscalYear ?? record.year) === Number(fiscalYear)
  ));
  const amountTco2e = numberOrNull(reportedImpact);
  if (amountTco2e === null || amountTco2e < 0) return retainedRecords;
  return [...retainedRecords, createImpactRecord({
    id: `impact-reported-${fiscalYear}`,
    stage: IMPACT_RECORD_TYPES.REPORTED,
    fiscalYear,
    amountTco2e,
    source: 'action-evidence',
  })];
}

function impactRecordsFromAnnualResults(actionId, annualResults, recordedAt) {
  return annualResults.flatMap((annualResult) => {
    const stageValues = [
      [IMPACT_RECORD_TYPES.ESTIMATED, annualResult.estimatedImpactTco2e],
      [IMPACT_RECORD_TYPES.REPORTED, annualResult.reportedImpactTco2e],
      [IMPACT_RECORD_TYPES.VERIFIED, annualResult.verifiedImpactTco2e],
    ];
    return stageValues.flatMap(([stage, amountTco2e]) => (
      numberOrNull(amountTco2e) === null
        ? []
        : [createImpactRecord({
          id: `${actionId}-${stage.toLowerCase()}-${annualResult.fiscalYear}`,
          stage,
          fiscalYear: annualResult.fiscalYear,
          amountTco2e,
          recordedAt,
          source: 'annual-result',
        })]
    ));
  });
}

function syncAnnualResultCompatibility(action) {
  const actionFiscalYear = Number(action.fiscalYear);
  const annualResults = (action.annualResults || [])
    .map(result => annualResultWithDerivedCreditAmounts(action, result))
    .filter(annualResult => (
      !Number.isInteger(actionFiscalYear)
      || annualResult.fiscalYear === actionFiscalYear
    ));
  const selectedResult = annualResults.find(
    annualResult => annualResult.fiscalYear === Number(action.fiscalYear),
  ) || null;
  return {
    ...action,
    annualResults,
    impactRecords: impactRecordsFromAnnualResults(action.id, annualResults, action.updatedAt),
    evidenceRecords: annualResults.flatMap(annualResult => annualResult.evidenceRecords
      .map(record => ({ ...record, fiscalYear: annualResult.fiscalYear }))),
    verificationStatus: selectedResult?.verificationStatus
      || CLIMATE_ACTION_VERIFICATION_STATUS.NOT_SUBMITTED,
    verifiedImpactTco2e: selectedResult?.verifiedImpactTco2e ?? null,
  };
}

function assertFiscalYearOwnedByAction(action, fiscalYear) {
  const actionFiscalYear = Number(action.fiscalYear);
  const selectedYear = Number(fiscalYear);
  if (Number.isInteger(actionFiscalYear) && actionFiscalYear !== selectedYear) {
    throw new RangeError(
      'Action FY' + actionFiscalYear + ' cannot store or mutate a FY'
        + selectedYear + ' result.',
    );
  }
}

function emptyAnnualResult(fiscalYear) {
  return {
    fiscalYear: Number(fiscalYear),
    estimatedImpactTco2e: null,
    reportedImpactTco2e: null,
    verifiedImpactTco2e: null,
    verificationStatus: CLIMATE_ACTION_VERIFICATION_STATUS.NOT_SUBMITTED,
    evidenceRecords: [],
    retirementBatches: [],
    financialEstimate: null,
    annualCostJpy: null,
    annualSavingsJpy: null,
  };
}

function annualResultCriticalValues(annualResult) {
  return {
    reportedImpactTco2e: annualResult.reportedImpactTco2e,
    evidenceRecords: annualResult.evidenceRecords,
    methodologyReference: annualResult.methodologyReference,
    registryReference: annualResult.registryReference,
    retirementDate: annualResult.retirementDate,
    creditStandard: annualResult.creditStandard,
    retiredQuantity: annualResult.retiredQuantity,
    retirement: annualResult.retirement,
    retirementBatches: annualResult.retirementBatches,
    vintageOrSerial: annualResult.vintageOrSerial,
  };
}

/** Insert or update one FY result and invalidate only that FY when verified inputs change. */
export function upsertAnnualResult(action, fiscalYear, annualResultDraft = {}) {
  const selectedYear = Number(fiscalYear);
  if (!Number.isInteger(selectedYear)) throw new RangeError('A fiscal year is required.');
  assertFiscalYearOwnedByAction(action, selectedYear);
  const existingResult = annualResultForYear(action, selectedYear) || emptyAnnualResult(selectedYear);
  const mergedResult = normalizeAnnualResult({
    ...existingResult,
    ...annualResultDraft,
    fiscalYear: selectedYear,
    evidenceRecords: annualResultDraft.evidenceRecords || existingResult.evidenceRecords,
  });
  const criticalChange = !valuesMatch(
    annualResultCriticalValues(existingResult),
    annualResultCriticalValues(mergedResult),
  );
  const simplifiedCreditEvidenceChanged = isRetiredCarbonCreditAction(action)
    && (
      !valuesMatch(existingResult.reportedImpactTco2e, mergedResult.reportedImpactTco2e)
      || !valuesMatch(existingResult.evidenceRecords, mergedResult.evidenceRecords)
    );
  if (simplifiedCreditEvidenceChanged && (mergedResult.retirementBatches || []).length > 0) {
    mergedResult.retirementBatches = mergedResult.retirementBatches.map(batch => (
      batch.verificationStatus === RETIREMENT_BATCH_STATUS.VERIFIED
        ? {
          ...batch,
          verificationStatus: RETIREMENT_BATCH_STATUS.UNDER_REVIEW,
          evidenceRecords: (batch.evidenceRecords || []).map(record => ({
            ...record, status: RETIREMENT_BATCH_STATUS.UNDER_REVIEW,
          })),
          audit: [...(batch.audit || []), {
            type: 'VERIFICATION_INVALIDATED', actor: 'Demo Admin', occurredAt: null,
          }],
        }
        : batch
    ));
  }
  if (existingResult.verificationStatus === CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED
    && criticalChange) {
    mergedResult.verifiedImpactTco2e = null;
    mergedResult.verificationStatus = mergedResult.reportedImpactTco2e === null
      ? CLIMATE_ACTION_VERIFICATION_STATUS.NOT_SUBMITTED
      : CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW;
  } else if (existingResult.verificationStatus === CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED) {
    mergedResult.verifiedImpactTco2e = existingResult.verifiedImpactTco2e;
    mergedResult.verificationStatus = CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED;
  } else if (existingResult.verificationStatus !== CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED) {
    mergedResult.verifiedImpactTco2e = null;
    if (mergedResult.verificationStatus === CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED) {
      mergedResult.verificationStatus = existingResult.verificationStatus;
    }
  }
  const annualResults = (action.annualResults || [])
    .filter(result => Number(result.fiscalYear) !== selectedYear)
    .map(normalizeAnnualResult);
  return syncAnnualResultCompatibility({
    ...action,
    annualResults: [...annualResults, mergedResult].sort(
      (left, right) => left.fiscalYear - right.fiscalYear,
    ),
  });
}

function validateEvidenceRecord(annualResult, evidenceRecord) {
  const fileName = String(evidenceRecord.name || '').trim();
  const extension = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
  const fileSizeBytes = numberOrNull(evidenceRecord.sizeBytes ?? evidenceRecord.size) || 0;
  if (!ACCEPTED_EVIDENCE_FILE_EXTENSIONS.includes(extension)) {
    throw new RangeError('Evidence file type is not supported.');
  }
  if (fileSizeBytes > MAX_EVIDENCE_FILE_SIZE_BYTES) {
    throw new RangeError('Evidence file must not exceed 25 MB.');
  }
  if ((annualResult.evidenceRecords || []).length >= MAX_EVIDENCE_FILE_COUNT) {
    throw new RangeError('A fiscal-year result can contain at most 10 evidence files.');
  }
}

/** Add one validated in-memory evidence file to one FY result. */
export function addEvidenceToAnnualResult(action, fiscalYear, evidenceRecord) {
  assertFiscalYearOwnedByAction(action, fiscalYear);
  const annualResult = annualResultForYear(action, fiscalYear) || emptyAnnualResult(fiscalYear);
  validateEvidenceRecord(annualResult, evidenceRecord);
  let evidenceIndex = annualResult.evidenceRecords.length + 1;
  let evidenceId = evidenceRecord.id || `${action.id}-evidence-${fiscalYear}-${evidenceIndex}`;
  const existingIds = new Set(annualResult.evidenceRecords.map(record => record.id));
  while (existingIds.has(evidenceId)) {
    evidenceIndex += 1;
    evidenceId = `${action.id}-evidence-${fiscalYear}-${evidenceIndex}`;
  }
  return upsertAnnualResult(action, fiscalYear, {
    evidenceRecords: [...annualResult.evidenceRecords, { ...evidenceRecord, id: evidenceId }],
  });
}

const retirementBatchesAcrossActions = (actions, fiscalYear, excludedActionId = null) => (
  (actions || []).flatMap((candidate) => {
    if (candidate.id === excludedActionId || !isRetiredCarbonCreditAction(candidate)) return [];
    const result = annualResultForYear(candidate, fiscalYear);
    return result ? normalizeRetirementBatches(result, candidate) : [];
  })
);

/** Return action-level review destinations supported by its pending retirement batches. */
export function retirementBatchReviewOptions(action, fiscalYear, allActions = [action]) {
  const annualResult = annualResultForYear(action, fiscalYear);
  const batches = normalizeRetirementBatches(annualResult || {}, action);
  if (batches.length === 0) return [];

  const underReview = batches.filter(batch => (
    batch.verificationStatus === RETIREMENT_BATCH_STATUS.UNDER_REVIEW
  ));
  if (underReview.length > 0) {
    const options = ['REJECTED'];
    if (actionIsApproved(action)) {
      const externalBatches = retirementBatchesAcrossActions(
        allActions,
        fiscalYear,
        action.id,
      );
      const validationContext = [...batches, ...externalBatches];
      if (underReview.every(batch => (
        validateRetirementBatch(batch, fiscalYear, validationContext).valid
      ))) options.unshift('VERIFIED');
    }
    return options;
  }

  const awaitingSubmission = batches.filter(batch => (
    [RETIREMENT_BATCH_STATUS.DRAFT, RETIREMENT_BATCH_STATUS.REJECTED]
      .includes(batch.verificationStatus)
  ));
  if (awaitingSubmission.length === 0 || !actionIsApproved(action)) return [];
  const externalBatches = retirementBatchesAcrossActions(allActions, fiscalYear, action.id);
  const validationContext = [...batches, ...externalBatches];
  return awaitingSubmission.every(batch => (
    validateRetirementBatch(batch, fiscalYear, validationContext).valid
  )) ? ['UNDER_REVIEW'] : [];
}

/** Review every currently eligible retirement batch as one atomic action update. */
function reviewRetirementBatches(
  action, fiscalYear, nextStatus, allActions, transition,
) {
  const availableOptions = retirementBatchReviewOptions(
    action,
    fiscalYear,
    allActions,
  );
  if (!availableOptions.includes(nextStatus)) {
    throw new RangeError('Retirement batches do not share the requested review transition.');
  }

  const annualResult = annualResultForYear(action, fiscalYear);
  const batches = normalizeRetirementBatches(annualResult || {}, action);
  const sourceStatuses = nextStatus === 'UNDER_REVIEW'
    ? [RETIREMENT_BATCH_STATUS.DRAFT, RETIREMENT_BATCH_STATUS.REJECTED]
    : [RETIREMENT_BATCH_STATUS.UNDER_REVIEW];
  const externalBatches = retirementBatchesAcrossActions(allActions, fiscalYear, action.id);
  const validationContext = [...batches, ...externalBatches];
  const audit = {
    actor: transition.actor || 'Demo Admin',
    occurredAt: transition.occurredAt || null,
  };
  const reviewedBatches = batches.map((batch) => {
    if (!sourceStatuses.includes(batch.verificationStatus)) return batch;
    if (nextStatus === 'UNDER_REVIEW') {
      return submitRetirementBatch(batch, fiscalYear, validationContext, audit);
    }
    if (nextStatus === 'VERIFIED') {
      return verifyRetirementBatch(batch, fiscalYear, validationContext, audit);
    }
    return rejectRetirementBatch(batch, transition.reason, audit);
  });

  return upsertAnnualResult(action, fiscalYear, {
    rejectionReason: nextStatus === 'REJECTED' ? String(transition.reason || '').trim() : null,
    retirementBatches: reviewedBatches,
  });
}

/** Add or edit one retirement batch; verified critical edits return it to review. */
export function upsertRetirementBatchForYear(
  action, fiscalYear, batchDraft, allActions = [], audit = {},
) {
  if (!isRetiredCarbonCreditAction(action)) {
    throw new RangeError('RETIREMENT_BATCH_REQUIRES_CREDIT_ACTION');
  }
  const annualResult = annualResultForYear(action, fiscalYear) || emptyAnnualResult(fiscalYear);
  const batches = normalizeRetirementBatches(annualResult, action);
  const existing = batches.find(batch => batch.id === batchDraft.id);
  const nextBatch = existing
    ? updateRetirementBatch(existing, batchDraft, audit)
    : normalizeRetirementBatch({
      ...batchDraft,
      id: batchDraft.id || `${action.id}-batch-${batches.length + 1}`,
    }, batches.length);
  const retained = batches.filter(batch => batch.id !== nextBatch.id);
  const externalBatches = retirementBatchesAcrossActions(allActions, fiscalYear, action.id);
  const duplicateCheck = validateRetirementBatch(
    nextBatch, fiscalYear, [...retained, ...externalBatches, nextBatch],
  );
  if (duplicateCheck.errors.includes('DUPLICATE_RETIREMENT_IDENTITY')) {
    throw new RangeError('DUPLICATE_RETIREMENT_IDENTITY');
  }
  return upsertAnnualResult(action, fiscalYear, {
    retirementBatches: [...retained, nextBatch],
  });
}

/** Submit one complete retirement batch for internal review. */
export function submitRetirementBatchForReview(
  action, fiscalYear, batchId, allActions = [], audit = {},
) {
  const annualResult = annualResultForYear(action, fiscalYear);
  const batches = normalizeRetirementBatches(annualResult || {}, action);
  const current = batches.find(batch => batch.id === batchId);
  if (!current) throw new RangeError('RETIREMENT_BATCH_NOT_FOUND');
  const externalBatches = retirementBatchesAcrossActions(allActions, fiscalYear, action.id);
  const submitted = submitRetirementBatch(
    current, fiscalYear, [...batches, ...externalBatches], audit,
  );
  return upsertAnnualResult(action, fiscalYear, {
    retirementBatches: batches.map(batch => (batch.id === batchId ? submitted : batch)),
  });
}

/** Atomically verify one retirement and include its quantity in the internal balance. */
export function verifyRetirementBatchForBalance(
  action, fiscalYear, batchId, allActions = [], audit = {},
) {
  if (!actionIsApproved(action)) throw new RangeError('ACTION_NOT_APPROVED');
  const annualResult = annualResultForYear(action, fiscalYear);
  const batches = normalizeRetirementBatches(annualResult || {}, action);
  const current = batches.find(batch => batch.id === batchId);
  if (!current) throw new RangeError('RETIREMENT_BATCH_NOT_FOUND');
  const externalBatches = retirementBatchesAcrossActions(allActions, fiscalYear, action.id);
  const verified = verifyRetirementBatch(
    current, fiscalYear, [...batches, ...externalBatches], audit,
  );
  return upsertAnnualResult(action, fiscalYear, {
    retirementBatches: batches.map(batch => (batch.id === batchId ? verified : batch)),
  });
}

function retiredCreditFieldsAreComplete(action, annualResult) {
  if (!isRetiredCarbonCreditAction(action)) return true;
  const retirementBatches = normalizeRetirementBatches(annualResult, action);
  if (retirementBatches.length > 0) {
    return retirementBatches.every(batch => validateRetirementBatch(
      batch, annualResult.fiscalYear || action.fiscalYear, retirementBatches,
    ).valid);
  }
  // Latest FE demo deliberately uses the same FY result + evidence flow for both
  // action types. Production registry controls remain a backend design concern.
  return true;
}

/** Submit one FY result after impact, evidence and credit-specific fields are complete. */
export function submitAnnualResultForReview(action, fiscalYear) {
  assertFiscalYearOwnedByAction(action, fiscalYear);
  const annualResult = annualResultForYear(action, fiscalYear);
  if (!annualResult || annualResult.reportedImpactTco2e === null) {
    throw new RangeError('Reported impact is required before review.');
  }
  if (annualResult.evidenceRecords.length === 0) {
    throw new RangeError('At least one evidence file is required before review.');
  }
  if (!retiredCreditFieldsAreComplete(action, annualResult)) {
    throw new RangeError('Retired carbon credit registry details are required before review.');
  }
  return upsertAnnualResult(action, fiscalYear, {
    evidenceRecords: annualResult.evidenceRecords.map(record => ({
      ...record,
      status: CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW,
    })),
    verificationStatus: CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW,
  });
}

/** Verify the full reported amount for one FY without changing any other FY result. */
export function verifyAnnualResult(action, fiscalYear) {
  assertFiscalYearOwnedByAction(action, fiscalYear);
  if (!actionIsApproved(action)) throw new RangeError('Only an approved action can be verified.');
  const annualResult = annualResultForYear(action, fiscalYear);
  if (!annualResult || annualResult.verificationStatus
    !== CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW) {
    throw new RangeError('The fiscal-year result must be under review before verification.');
  }
  if (annualResult.reportedImpactTco2e === null || annualResult.evidenceRecords.length === 0) {
    throw new RangeError('Reported impact and evidence are required before verification.');
  }
  if (!retiredCreditFieldsAreComplete(action, annualResult)) {
    throw new RangeError('Retired carbon credit registry details are required before verification.');
  }
  const verifiedResult = normalizeAnnualResult({
    ...annualResult,
    verifiedImpactTco2e: annualResult.reportedImpactTco2e,
    verificationStatus: CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED,
    evidenceRecords: annualResult.evidenceRecords.map(record => ({
      ...record,
      status: CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED,
    })),
  });
  const annualResults = [verifiedResult];
  return syncAnnualResultCompatibility({ ...action, annualResults });
}

/** Reject one FY review with a required reason while preserving its submitted amount. */
export function rejectAnnualResult(action, fiscalYear, reason) {
  assertFiscalYearOwnedByAction(action, fiscalYear);
  const rejectionReason = String(reason || '').trim();
  if (!rejectionReason) throw new RangeError('A rejection reason is required.');
  const annualResult = annualResultForYear(action, fiscalYear);
  if (!annualResult || annualResult.verificationStatus
    !== CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW) {
    throw new RangeError('Only a result under review can be rejected.');
  }
  return upsertAnnualResult(action, fiscalYear, {
    evidenceRecords: annualResult.evidenceRecords.map(record => ({
      ...record,
      status: CLIMATE_ACTION_VERIFICATION_STATUS.REJECTED,
    })),
    verificationStatus: CLIMATE_ACTION_VERIFICATION_STATUS.REJECTED,
    rejectionReason,
  });
}

/**
 * Apply one FY evidence transition to every selected action as a single state change.
 * All domain mutations are calculated before a new actions array is returned, so an
 * invalid row cannot leave the in-memory demo in a partially updated state.
 */
export function reviewAnnualResults(actions, actionIds, fiscalYear, nextStatus, transition = {}) {
  const selectedIds = new Set(actionIds || []);
  if (selectedIds.size === 0) return actions;

  const supportedStatuses = new Set(['UNDER_REVIEW', 'VERIFIED', 'REJECTED']);
  if (!supportedStatuses.has(nextStatus)) {
    throw new RangeError('FY evidence review status is not supported.');
  }

  const selectedActions = actions.filter(action => selectedIds.has(action.id));
  if (selectedActions.length !== selectedIds.size) {
    throw new RangeError('One or more selected climate actions no longer exist.');
  }

  const updatedById = new Map(selectedActions.map((action) => {
    const annualResult = annualResultForYear(action, fiscalYear);
    if ((annualResult?.retirementBatches || []).length > 0) {
      return [action.id, reviewRetirementBatches(
        action,
        fiscalYear,
        nextStatus,
        actions,
        transition,
      )];
    }
    if (nextStatus === 'UNDER_REVIEW') {
      if (!actionIsApproved(action)) {
        throw new RangeError('Only an in-progress or completed action can submit FY evidence.');
      }
      return [action.id, submitAnnualResultForReview(action, fiscalYear)];
    }
    if (nextStatus === 'VERIFIED') {
      return [action.id, verifyAnnualResult(action, fiscalYear)];
    }
    return [action.id, rejectAnnualResult(action, fiscalYear, transition.reason)];
  }));

  return actions.map(action => updatedById.get(action.id) || action);
}

/** Record an action-level approve or not-approved decision without verifying any FY. */
export function reviewClimateAction(action, reviewStatus, reason = null) {
  if (![CLIMATE_ACTION_REVIEW_STATUS.APPROVED, CLIMATE_ACTION_REVIEW_STATUS.NOT_APPROVED]
    .includes(reviewStatus)) throw new RangeError('Action review decision is not supported.');
  const reviewReason = String(reason || '').trim();
  if (reviewStatus === CLIMATE_ACTION_REVIEW_STATUS.NOT_APPROVED && !reviewReason) {
    throw new RangeError('A not-approved reason is required.');
  }
  const isApproved = reviewStatus === CLIMATE_ACTION_REVIEW_STATUS.APPROVED;
  const workflowStatus = isApproved
    ? CLIMATE_ACTION_WORKFLOW_STATUS.IN_PROGRESS
    : CLIMATE_ACTION_WORKFLOW_STATUS.NOT_APPROVED;
  return {
    ...action,
    workflowStatus,
    actionStatus: workflowStatus,
    status: PRESENTATION_STATUS[workflowStatus],
    readiness: readinessForWorkflow(action, workflowStatus),
    notApprovedReason: isApproved ? null : reviewReason,
  };
}

/** Approve an action for Climate Action impact reporting. */
export function approveClimateAction(action) {
  return reviewClimateAction(action, CLIMATE_ACTION_REVIEW_STATUS.APPROVED);
}

/** Mark an action not approved with its required business reason. */
export function notApproveClimateAction(action, reason) {
  return reviewClimateAction(action, CLIMATE_ACTION_REVIEW_STATUS.NOT_APPROVED, reason);
}

function evidenceRecordsForDemoAction(action) {
  const references = ['certificateReference', 'evidenceReference', 'methodologyReference', 'registryReference']
    .map(fieldKey => action.fieldValues?.[fieldKey])
    .filter(Boolean);
  return references.map((reference, index) => ({
    id: `${action.id}-evidence-${index + 1}`,
    reference,
    status: action.readiness === 'verified' ? 'VERIFIED' : 'ATTACHED',
    recordedAt: action.updatedAt,
  }));
}

function canonicalizeDemoAction(action, index) {
  const workflowStatus = canonicalWorkflowStatus(action.workflowStatus || action.status)
    || CLIMATE_ACTION_WORKFLOW_STATUS.DRAFT;
  const fiscalYear = Number(action.fiscalYear || DEFAULT_DEMO_YEAR);
  const impactRecords = (action.impactRecords || []).map(impactRecord => ({ ...impactRecord }));
  const metadata = solutionMetadata(action.solutionCode);
  const legacyEvidenceRecords = (action.evidenceRecords || evidenceRecordsForDemoAction(action))
    .map(evidenceRecord => ({ ...evidenceRecord }));
  const legacyYears = [...new Set(impactRecords.map(record => Number(record.fiscalYear)))]
    .filter(Number.isInteger);
  const annualResults = (action.annualResults?.length
    ? action.annualResults.map(normalizeAnnualResult)
    : legacyYears.map(year => legacyAnnualResultForYear({
      ...action,
      impactRecords,
      evidenceRecords: legacyEvidenceRecords,
    }, year)).filter(Boolean))
    .filter(annualResult => annualResult.fiscalYear === fiscalYear);
  const actionType = canonicalActionType(action);
  const canonicalAction = {
    ...action,
    solutionName: action.solutionName || action.plannedAction || null,
    scope: action.scope || action.scopeGroup || metadata.scopeGroup,
    scopeGroup: action.scopeGroup || action.scope || metadata.scopeGroup,
    category: action.category || metadata.category,
    subcategory: action.subcategory || null,
    actionType,
    accountingClassification: actionType === ACTION_TYPES.CARBON_CREDIT_RETIREMENT
      ? ACCOUNTING_CLASSIFICATIONS.RETIRED_CARBON_CREDIT
      : (action.accountingClassification || metadata.accountingClassification),
    owner: action.owner ? { ...action.owner } : { ...DEFAULT_ACTION_OWNER },
    dueDate: action.dueDate || `2025-12-${String(20 + index).padStart(2, '0')}`,
    activePeriod: action.activePeriod
      ? { ...action.activePeriod }
      : { startDate: `${fiscalYear}-01-01`, endDate: `${fiscalYear}-12-31` },
    fiscalYear,
    workflowStatus,
    actionStatus: workflowStatus,
    status: PRESENTATION_STATUS[workflowStatus],
    annualResults,
    history: (action.history || [{
      id: `${action.id}-history-1`,
      fromStatus: null,
      toStatus: workflowStatus,
      reason: null,
      reasonKey: 'initialized',
      actor: DEFAULT_ACTION_OWNER.name,
      occurredAt: action.updatedAt,
    }]).map(historyEntry => ({ ...historyEntry })),
    recommendationSnapshot: action.recommendationSnapshot
      ? deepFreeze({ ...action.recommendationSnapshot })
      : undefined,
  };
  delete canonicalAction.activePeriod;
  delete canonicalAction.actionKind;
  delete canonicalAction.creditAllocationScope;
  delete canonicalAction.isApprovedForCompensation;
  delete canonicalAction.requestedForCompensationReview;
  delete canonicalAction.reviewStatus;
  return syncAnnualResultCompatibility(canonicalAction);
}

function readinessForWorkflow(action, workflowStatus) {
  if (canonicalVerificationStatus(action.verificationStatus)
    === CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED) return 'verified';
  if (workflowStatus === CLIMATE_ACTION_WORKFLOW_STATUS.NOT_APPROVED) return 'needsEvidence';
  return actionHasRequiredData(action) ? 'ready' : 'needsData';
}

function verificationHistory(action, nextStatus, transition) {
  return {
    id: action.id + '-history-' + ((action.history || []).length + 1),
    historyType: 'VERIFICATION',
    fromVerificationStatus: canonicalVerificationStatus(action.verificationStatus),
    toVerificationStatus: nextStatus,
    reason: null,
    actor: transition.actor || DEFAULT_ACTION_OWNER.name,
    occurredAt: transition.occurredAt || action.updatedAt,
  };
}

/** Compatibility adapter for existing callers; verification remains FY-specific. */
export function transitionActionVerification(action, nextStatus, transition = {}) {
  const canonicalNextStatus = canonicalVerificationStatus(nextStatus);
  if (canonicalNextStatus !== CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED) {
    throw new RangeError('Verification can only transition to VERIFIED.');
  }
  const fiscalYear = Number(transition.fiscalYear || action.fiscalYear);
  const annualResult = annualResultForYear(action, fiscalYear);
  if (annualResult?.verificationStatus === CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED) return action;
  const requestedImpact = numberOrNull(transition.verifiedImpactTco2e);
  if (requestedImpact !== null
    && Math.abs(requestedImpact - annualResult?.reportedImpactTco2e) > Number.EPSILON) {
    throw new RangeError('Partial verification is not supported in this demo.');
  }
  const occurredAt = transition.occurredAt || action.updatedAt;
  const underReviewAction = annualResult?.verificationStatus
    === CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW
    ? action
    : submitAnnualResultForReview(action, fiscalYear);
  const verifiedAction = verifyAnnualResult(underReviewAction, fiscalYear);
  return {
    ...verifiedAction,
    readiness: 'verified',
    history: [...(action.history || []), verificationHistory(action, canonicalNextStatus, transition)],
    updatedAt: occurredAt,
  };
}

function workflowHistory(action, currentStatus, nextStatus, transition, reason) {
  return {
    id: action.id + '-history-' + ((action.history || []).length + 1),
    historyType: 'WORKFLOW',
    fromStatus: currentStatus,
    toStatus: nextStatus,
    reason: reason || null,
    actor: transition.actor || DEFAULT_ACTION_OWNER.name,
    occurredAt: transition.occurredAt || action.updatedAt,
  };
}

/** Apply one legal action workflow transition and keep verification independent. */
export function transitionActionWorkflow(action, nextStatus, transition = {}) {
  if (nextStatus === CLIMATE_ACTION_WORKFLOW_STATUS.VERIFIED) {
    return transitionActionVerification(
      action,
      CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED,
      transition,
    );
  }
  const currentStatus = canonicalWorkflowStatus(action.workflowStatus || action.status);
  const canonicalNextStatus = canonicalWorkflowStatus(nextStatus);
  if (currentStatus === canonicalNextStatus) return action;
  if (!DELIVERY_STATUS_TRANSITIONS[currentStatus]?.includes(canonicalNextStatus)) {
    throw new RangeError(
      'Action cannot transition from ' + currentStatus + ' to ' + canonicalNextStatus + '.',
    );
  }
  const reason = String(transition.reason || '').trim();
  if (canonicalNextStatus === CLIMATE_ACTION_WORKFLOW_STATUS.NOT_APPROVED && !reason) {
    throw new RangeError('A not-approved reason is required.');
  }
  const historyEntry = workflowHistory(
    action,
    currentStatus,
    canonicalNextStatus,
    transition,
    reason,
  );
  const isNotApproved = canonicalNextStatus === CLIMATE_ACTION_WORKFLOW_STATUS.NOT_APPROVED;
  return {
    ...action,
    workflowStatus: canonicalNextStatus,
    actionStatus: canonicalNextStatus,
    status: PRESENTATION_STATUS[canonicalNextStatus],
    verificationStatus: canonicalVerificationStatus(action.verificationStatus),
    readiness: readinessForWorkflow(action, canonicalNextStatus),
    notApprovedReason: isNotApproved ? reason : null,
    rejectionReason: isNotApproved ? reason : null,
    impactRecords: (action.impactRecords || []).map(record => ({ ...record })),
    history: [...(action.history || []), historyEntry],
    updatedAt: transition.occurredAt || action.updatedAt,
  };
}

function firstEditedOrExistingValue(action, actionDraft, fieldNames) {
  const editedField = fieldNames.find(fieldName => (
    Object.prototype.hasOwnProperty.call(actionDraft, fieldName)
  ));
  if (editedField) return actionDraft[editedField];
  const existingField = fieldNames.find(fieldName => (
    action[fieldName] !== undefined && action[fieldName] !== null
  ));
  return existingField ? action[existingField] : null;
}

function canonicalMasterValue(value) {
  const normalizedValue = String(value ?? '').trim();
  return normalizedValue === '' ? null : normalizedValue;
}

function canonicalActionTypeValue(value) {
  const normalizedValue = canonicalMasterValue(value);
  return normalizedValue?.replace(/[^a-z0-9]/gi, '').toLowerCase() || null;
}

function masterVerificationContext(action, actionDraft = {}) {
  return {
    solutionCode: canonicalMasterValue(firstEditedOrExistingValue(
      action, actionDraft, ['solutionCode'],
    )),
    actionType: canonicalActionTypeValue(firstEditedOrExistingValue(
      action, actionDraft, ['actionType', 'accountingClassification'],
    )),
    scope: canonicalMasterValue(firstEditedOrExistingValue(
      action, actionDraft, ['scope', 'scopeGroup'],
    )),
    category: canonicalMasterValue(firstEditedOrExistingValue(
      action, actionDraft, ['category'],
    )),
    subcategory: canonicalMasterValue(firstEditedOrExistingValue(
      action, actionDraft, ['subcategory'],
    )),
    boundary: canonicalMasterValue(firstEditedOrExistingValue(
      action, actionDraft, ['boundaryId', 'boundary', 'boundaryName'],
    )),
  };
}

/** Return whether a master accounting edit affects every FY verification. */
export function changesMasterVerificationCriticalFields(action, actionDraft) {
  const hasMasterEdit = MASTER_VERIFICATION_CRITICAL_FIELDS.some(fieldName => (
    Object.prototype.hasOwnProperty.call(actionDraft, fieldName)
  ));
  const explicitlyChangedType = ['actionType', 'accountingClassification'].some(fieldName => (
    Object.prototype.hasOwnProperty.call(action, fieldName)
    && Object.prototype.hasOwnProperty.call(actionDraft, fieldName)
    && canonicalActionTypeValue(action[fieldName])
      !== canonicalActionTypeValue(actionDraft[fieldName])
  ));
  return hasMasterEdit && (
    explicitlyChangedType
    || !valuesMatch(
      masterVerificationContext(action),
      masterVerificationContext(action, actionDraft),
    )
  );
}

function changedAnnualResultFiscalYears(action, actionDraft) {
  const changedFiscalYears = new Set();
  if (Object.prototype.hasOwnProperty.call(actionDraft, 'annualResults')) {
    const existingResults = canonicalAnnualResultsForAction(action);
    const draftResults = (actionDraft.annualResults || []).map(normalizeAnnualResult);
    const fiscalYears = new Set([
      ...existingResults.map(result => Number(result.fiscalYear)),
      ...draftResults.map(result => Number(result.fiscalYear)),
    ]);
    fiscalYears.forEach((fiscalYear) => {
      const existingResult = existingResults.find(result => result.fiscalYear === fiscalYear)
        || emptyAnnualResult(fiscalYear);
      const draftResult = draftResults.find(result => result.fiscalYear === fiscalYear)
        || emptyAnnualResult(fiscalYear);
      if (!valuesMatch(
        annualResultCriticalValues(existingResult),
        annualResultCriticalValues(draftResult),
      )) changedFiscalYears.add(fiscalYear);
    });
  }
  const hasLegacyAnnualEdit = ANNUAL_VERIFICATION_CRITICAL_FIELDS.some(fieldName => (
    Object.prototype.hasOwnProperty.call(actionDraft, fieldName)
    && !valuesMatch(action[fieldName], actionDraft[fieldName])
  ));
  if (hasLegacyAnnualEdit) {
    const selectedYear = Number(actionDraft.fiscalYear || action.fiscalYear);
    if (Number.isInteger(selectedYear)) changedFiscalYears.add(selectedYear);
  }
  return [...changedFiscalYears];
}

function changesAnnualVerificationCriticalFields(action, actionDraft) {
  return changedAnnualResultFiscalYears(action, actionDraft).length > 0;
}

/** Return whether a saved edit changes master accounting or selected-FY evidence inputs. */
export function changesVerificationCriticalFields(action, actionDraft) {
  return changesMasterVerificationCriticalFields(action, actionDraft)
    || changesAnnualVerificationCriticalFields(action, actionDraft);
}

const finiteNumber = numberOrNull;

/** Return an inventory record with a total derived from its three scopes. */
export function inventoryForYear(year, inventories = DEMO_INVENTORIES) {
  const inventory = inventories.find(candidate => candidate.year === Number(year));
  if (!inventory) return null;
  return {
    ...inventory,
    total: inventory.scope1 + inventory.scope2 + inventory.scope3,
  };
}

function targetValidationContext(target, inventories) {
  const baselineYear = Number(target?.baselineYear);
  const targetYear = Number(target?.targetYear);
  const baselineInventory = inventoryForYear(baselineYear, inventories);
  return {
    baselineInventory,
    baselineValue: baselineInventory?.[target?.scope],
    baselineYear,
    targetYear,
    trajectoryType: target?.trajectoryType || TARGET_TRAJECTORY_TYPES.LINEAR,
  };
}

/** Validate a linear or custom target without mutating form input. */
export function validateScopeTarget(target, inventories = DEMO_INVENTORIES) {
  const errors = [];
  const context = targetValidationContext(target, inventories);
  if (!['scope1', 'scope2', 'scope3'].includes(target?.scope)) errors.push('INVALID_SCOPE');
  if (!Number.isFinite(context.baselineValue)) errors.push('BASELINE_INVENTORY_REQUIRED');
  if (!Number.isInteger(context.targetYear) || context.targetYear <= context.baselineYear) {
    errors.push('TARGET_YEAR_MUST_FOLLOW_BASELINE');
  }
  if (!Object.values(TARGET_TRAJECTORY_TYPES).includes(context.trajectoryType)) {
    errors.push('INVALID_TRAJECTORY_TYPE');
  }
  if (context.trajectoryType === TARGET_TRAJECTORY_TYPES.LINEAR) {
    const reductionPercent = Number(target?.reductionPercent);
    const targetYearEmissions = numberOrNull(target?.targetYearEmissions);
    if ((!Number.isFinite(reductionPercent) || reductionPercent <= 0 || reductionPercent > 100)
      && (targetYearEmissions === null || targetYearEmissions < 0
        || targetYearEmissions >= context.baselineValue)) {
      errors.push('LINEAR_TARGET_REDUCTION_REQUIRED');
    }
  }
  if (context.trajectoryType === TARGET_TRAJECTORY_TYPES.CUSTOM) {
    const milestones = Array.isArray(target?.milestones) ? target.milestones : [];
    const normalizedYears = milestones.map(milestone => Number(milestone.year));
    const hasValidValues = milestones.every(milestone => (
      Number.isInteger(Number(milestone.year))
      && numberOrNull(milestone.targetEmissions) !== null
      && Number(milestone.targetEmissions) >= 0
    ));
    const strictlyIncreasing = normalizedYears.every(
      (milestoneYear, index) => index === 0 || milestoneYear > normalizedYears[index - 1],
    );
    const yearsInRange = normalizedYears.every(
      milestoneYear => milestoneYear > context.baselineYear && milestoneYear <= context.targetYear,
    );
    if (!milestones.length || !hasValidValues || !strictlyIncreasing || !yearsInRange
      || normalizedYears[normalizedYears.length - 1] !== context.targetYear) {
      errors.push('CUSTOM_MILESTONES_INVALID');
    }
  }
  return { valid: errors.length === 0, errors };
}

/** Normalize one valid scope target and derive its final reduction consistently. */
export function normalizeScopeTarget(target, inventories = DEMO_INVENTORIES) {
  const validation = validateScopeTarget(target, inventories);
  if (!validation.valid) throw new RangeError(validation.errors.join(', '));
  const context = targetValidationContext(target, inventories);
  if (context.trajectoryType === TARGET_TRAJECTORY_TYPES.CUSTOM) {
    const milestones = target.milestones.map(milestone => ({
      year: Number(milestone.year),
      targetEmissions: Number(milestone.targetEmissions),
    }));
    const targetYearEmissions = milestones[milestones.length - 1].targetEmissions;
    return {
      scope: target.scope,
      trajectoryType: context.trajectoryType,
      baselineYear: context.baselineYear,
      targetYear: context.targetYear,
      milestones,
      targetYearEmissions,
      reductionPercent: ((context.baselineValue - targetYearEmissions) / context.baselineValue) * 100,
    };
  }
  const suppliedEndValue = numberOrNull(target.targetYearEmissions);
  const suppliedReduction = Number(target.reductionPercent);
  const reductionPercent = suppliedReduction > 0 && suppliedReduction <= 100
    ? suppliedReduction
    : ((context.baselineValue - suppliedEndValue) / context.baselineValue) * 100;
  return {
    scope: target.scope,
    trajectoryType: TARGET_TRAJECTORY_TYPES.LINEAR,
    baselineYear: context.baselineYear,
    targetYear: context.targetYear,
    reductionPercent,
    targetYearEmissions: context.baselineValue * (1 - (reductionPercent / 100)),
  };
}

/** Validate and normalize a complete three-scope target save as one atomic value. */
export function normalizeTargetBatch(targets, inventories = DEMO_INVENTORIES) {
  const requiredScopes = ['scope1', 'scope2', 'scope3'];
  if (!targets || !requiredScopes.every(scope => targets[scope])) {
    throw new RangeError('Scope 1, Scope 2 and Scope 3 targets are required.');
  }
  return Object.fromEntries(requiredScopes.map(
    scope => [scope, normalizeScopeTarget({ ...targets[scope], scope }, inventories)],
  ));
}

function interpolateTargetAnchors(anchors, selectedYear) {
  const rightIndex = anchors.findIndex(anchor => anchor.year >= selectedYear);
  if (rightIndex < 0) return null;
  if (anchors[rightIndex].year === selectedYear) return anchors[rightIndex].targetEmissions;
  const left = anchors[rightIndex - 1];
  const right = anchors[rightIndex];
  const progress = (selectedYear - left.year) / (right.year - left.year);
  return left.targetEmissions + ((right.targetEmissions - left.targetEmissions) * progress);
}

/** Calculate one annual target using either linear or custom milestone interpolation. */
export function annualTargetForScope(target, year, inventories = DEMO_INVENTORIES) {
  if (!target) return null;
  let normalizedTarget;
  try {
    normalizedTarget = normalizeScopeTarget(target, inventories);
  } catch (error) {
    if (error instanceof RangeError) return null;
    throw error;
  }
  const baselineValue = inventoryForYear(normalizedTarget.baselineYear, inventories)?.[target.scope];
  const selectedYear = Number(year);
  if (selectedYear < normalizedTarget.baselineYear || selectedYear > normalizedTarget.targetYear) return null;
  const milestones = normalizedTarget.trajectoryType === TARGET_TRAJECTORY_TYPES.CUSTOM
    ? normalizedTarget.milestones
    : [{ year: normalizedTarget.targetYear, targetEmissions: normalizedTarget.targetYearEmissions }];
  return interpolateTargetAnchors([
    { year: normalizedTarget.baselineYear, targetEmissions: baselineValue },
    ...milestones,
  ], selectedYear);
}

/** Build a transparent year-by-year preview for one linear scope target. */
export function buildLinearTargetPreview(target, inventories = DEMO_INVENTORIES) {
  if (!target?.scope) return null;
  const baselineYear = Number(target.baselineYear);
  const targetYear = Number(target.targetYear);
  const reductionPercent = Number(target.reductionPercent);
  const baselineInventory = inventoryForYear(baselineYear, inventories);
  const baselineValue = baselineInventory?.[target.scope];
  const duration = targetYear - baselineYear;
  const valid = Number.isFinite(baselineValue)
    && Number.isFinite(reductionPercent)
    && duration > 0
    && duration <= 50
    && reductionPercent > 0
    && reductionPercent <= 100;
  if (!valid) return null;

  const endTarget = baselineValue * (1 - (reductionPercent / 100));
  const annualChange = (endTarget - baselineValue) / duration;
  const rows = Array.from({ length: duration + 1 }, (_, index) => {
    const year = baselineYear + index;
    const inventory = inventoryForYear(year, inventories);
    const actual = inventory?.[target.scope] ?? null;
    const targetValue = baselineValue + (annualChange * index);
    return {
      year,
      actual,
      target: targetValue,
      variance: actual === null ? null : actual - targetValue,
      isBaseline: year === baselineYear,
    };
  });

  return {
    scope: target.scope,
    baselineYear,
    targetYear,
    baselineValue,
    endTarget,
    annualChange,
    rows,
  };
}

/** Build the same preview contract for a custom milestone trajectory. */
export function buildCustomTargetPreview(target, inventories = DEMO_INVENTORIES) {
  let normalizedTarget;
  try {
    normalizedTarget = normalizeScopeTarget({
      ...target,
      trajectoryType: TARGET_TRAJECTORY_TYPES.CUSTOM,
    }, inventories);
  } catch (error) {
    if (error instanceof RangeError) return null;
    throw error;
  }
  const baselineValue = inventoryForYear(normalizedTarget.baselineYear, inventories)?.[target.scope];
  const rows = Array.from(
    { length: normalizedTarget.targetYear - normalizedTarget.baselineYear + 1 },
    (_, index) => {
      const year = normalizedTarget.baselineYear + index;
      const actual = inventoryForYear(year, inventories)?.[target.scope] ?? null;
      const targetValue = annualTargetForScope(normalizedTarget, year, inventories);
      return {
        year,
        actual,
        target: targetValue,
        variance: actual === null ? null : actual - targetValue,
        isBaseline: year === normalizedTarget.baselineYear,
      };
    },
  );
  return {
    ...normalizedTarget,
    baselineValue,
    endTarget: normalizedTarget.targetYearEmissions,
    annualChange: null,
    rows,
  };
}

/** Route preview generation by trajectory while preserving the linear helper contract. */
export function buildTargetPreview(target, inventories = DEMO_INVENTORIES) {
  return target?.trajectoryType === TARGET_TRAJECTORY_TYPES.CUSTOM
    ? buildCustomTargetPreview(target, inventories)
    : buildLinearTargetPreview(target, inventories);
}

/** Build annual target values per scope and derive Total only when all scopes exist. */
export function annualTargetsForYear(targets, year, inventories = DEMO_INVENTORIES) {
  const scope1 = annualTargetForScope(targets?.scope1, year, inventories);
  const scope2 = annualTargetForScope(targets?.scope2, year, inventories);
  const scope3 = annualTargetForScope(targets?.scope3, year, inventories);
  const total = [scope1, scope2, scope3].every(Number.isFinite)
    ? scope1 + scope2 + scope3
    : null;
  return { total, scope1, scope2, scope3 };
}

/** Resolve the one target cycle owning an FY without coupling it to the outer FY selector. */
export function targetContextForYear({ targetCycles, targets, year }) {
  if (!Array.isArray(targetCycles)) return { cycle: null, targets };
  const cycles = normalizeTargetCycles(targetCycles, targets);
  const cycle = resolveTargetCycleForYear(cycles, year);
  return { cycle, targets: cycle?.targets || null };
}

/** Build the Actual and Target series used by the pathway chart. */
export function buildPathwaySeries({ targets, scope, inventories = DEMO_INVENTORIES }) {
  const configuredTargets = Object.values(targets || {}).filter(target => (
    target
    && Number.isFinite(Number(target.baselineYear))
    && Number.isFinite(Number(target.targetYear))
  ));
  const selectedTargets = scope === CLIMATE_ACTION_SCOPES.TOTAL
    ? configuredTargets
    : [targets?.[scope]].filter(Boolean);
  if (selectedTargets.length === 0) return [];
  const firstYear = scope === CLIMATE_ACTION_SCOPES.TOTAL
    ? Math.max(...selectedTargets.map(target => Number(target.baselineYear)))
    : Number(selectedTargets[0].baselineYear);
  const lastYear = scope === CLIMATE_ACTION_SCOPES.TOTAL
    ? Math.min(...selectedTargets.map(target => Number(target.targetYear)))
    : Number(selectedTargets[0].targetYear);
  if (!Number.isInteger(firstYear) || !Number.isInteger(lastYear) || lastYear < firstYear) {
    return [];
  }
  return Array.from({ length: lastYear - firstYear + 1 }, (_, index) => {
    const year = firstYear + index;
    const inventory = inventoryForYear(year, inventories);
    const annualTargets = annualTargetsForYear(targets, year, inventories);
    return {
      year,
      actual: inventory?.[scope] ?? null,
      target: annualTargets[scope],
    };
  });
}

/** Sum only verified retired carbon credits for the selected FY. */
export function verifiedRetiredOffsetsForYear(actions = [], year) {
  return carbonCreditCompensationForYear(actions, year, 0).verifiedRetiredCreditsTco2e;
}

/** Return the four derived carbon-position values for the selected fiscal year. */
export function carbonPosition({
  year, targets, targetCycles, actions = [], inventories = DEMO_INVENTORIES,
}) {
  const inventory = inventoryForYear(year, inventories);
  const targetContext = targetContextForYear({ targetCycles, targets, year });
  const annualTargets = annualTargetsForYear(targetContext.targets, year, inventories);
  const gross = inventory?.total ?? 0;
  const target = annualTargets.total;
  const creditCompensation = carbonCreditCompensationForYear(actions, year, gross);
  const retiredOffsets = creditCompensation.verifiedRetiredCreditsTco2e;
  const residual = Math.max(gross - retiredOffsets, 0);
  const surplusOffsets = Math.max(retiredOffsets - gross, 0);
  return {
    gross,
    target,
    gap: Number.isFinite(target) ? gross - target : null,
    retiredOffsets,
    residual,
    surplusOffsets,
    offsetCoverage: gross > 0 ? Math.min((retiredOffsets / gross) * 100, 100) : 0,
    creditCompensation,
    inventory,
    annualTargets,
    targetCycle: targetContext.cycle,
  };
}

/** Return whether every required catalog field contains a meaningful value. */
export function actionHasRequiredData(action) {
  if (isRetiredCarbonCreditAction(action)) {
    return Boolean(
      String(action.solutionName || action.plannedAction || '').trim()
      && String(action.scope || action.scopeGroup || '').trim()
      && String(action.category || '').trim()
      && String(action.boundary || action.boundaryName || '').trim()
      && Number.isInteger(Number(action.fiscalYear))
      && submittedImpactForYear(action, action.fiscalYear) !== null
    );
  }
  const catalogEntry = SOLUTION_CATALOG[action.solutionCode];
  if (!catalogEntry) {
    return Boolean(
      String(action.solutionName || action.plannedAction || '').trim()
      && Number.isInteger(Number(action.fiscalYear))
      && submittedImpactForYear(action, action.fiscalYear) !== null,
    );
  }
  return catalogEntry.requiredFieldKeys.every(fieldKey => {
    const fieldValue = action.fieldValues?.[fieldKey];
    return fieldValue !== null && fieldValue !== undefined && String(fieldValue).trim() !== '';
  });
}

/** Require either an evidence record or a solution-specific evidence/reference field. */
export function actionHasVerificationEvidence(action, fiscalYear = action.fiscalYear) {
  const annualResult = annualResultForYear(action, fiscalYear);
  if (Array.isArray(action.annualResults)) {
    return (annualResult?.evidenceRecords || []).length > 0;
  }
  const hasEvidenceRecord = (action.evidenceRecords || []).some(record => (
    (record.fiscalYear === undefined || Number(record.fiscalYear) === Number(fiscalYear))
    && String(record.reference || record.url || record.name || '').trim() !== ''
  ));
  if (hasEvidenceRecord) return true;
  const catalogEntry = SOLUTION_CATALOG[action.solutionCode];
  return (catalogEntry?.fieldKeys || [])
    .filter(fieldKey => /reference|methodology/i.test(fieldKey))
    .some(fieldKey => String(action.fieldValues?.[fieldKey] || '').trim() !== '');
}

/** One shared UI/model guard for the Evidence pending -> Verified decision. */
export function actionCanBeVerified(action, fiscalYear = action.fiscalYear) {
  const annualResult = annualResultForYear(action, fiscalYear);
  const hasSubmittedImpact = annualResult?.reportedImpactTco2e !== null
    && annualResult?.reportedImpactTco2e !== undefined;
  return actionHasRequiredData(action)
    && hasSubmittedImpact
    && actionHasVerificationEvidence(action, fiscalYear)
    && retiredCreditFieldsAreComplete(action, annualResult || {});
}

/** Calculate the small set of deterministic impact examples supported by the demo. */
export function calculateDemoActionImpact(action) {
  if (action.solutionCode === 'green_electricity') {
    const renewableKwh = finiteNumber(action.fieldValues?.renewableKwh);
    const factor = finiteNumber(action.fieldValues?.emissionFactorTco2ePerKwh)
      ?? ELECTRICITY_FACTOR_TCO2E_PER_KWH;
    return renewableKwh === null ? null : renewableKwh * factor;
  }
  if (action.solutionCode === 'dhl_gogreen') {
    return finiteNumber(action.fieldValues?.allocatedTco2e);
  }
  if (action.solutionCode === 'retired_offset') {
    return finiteNumber(action.fieldValues?.retiredQuantity);
  }
  return null;
}

/** Return the first three eligible recommendations in deterministic rank order. */
export function visibleRecommendations({
  recommendations, year, dismissedIds = [], savedRecommendationIds = [], actions = [], limit = 3,
  keepSaved = false,
}) {
  const candidates = recommendations
    .filter(recommendation => recommendation.fiscalYear === Number(year))
    .filter(recommendation => !dismissedIds.includes(recommendation.id))
    .filter(recommendation => keepSaved || !savedRecommendationIds.includes(recommendation.id))
    .filter(recommendation => !actions.some(
      action => (
        actionDuplicateKey(action) === recommendationDuplicateKey(recommendation)
        && (!keepSaved || action.sourceRecommendationId !== recommendation.id)
      ),
    ))
    .sort((left, right) => left.rank - right.rank);
  return Number.isFinite(limit) ? candidates.slice(0, limit) : candidates;
}

function boundaryForRecord(record) {
  if (record.solutionCode === 'retired_offset' && record.fieldValues?.registryReference) {
    return `registry:${String(record.fieldValues.registryReference).trim().toLowerCase()}`;
  }
  return record.boundary
    || record.fieldValues?.office
    || record.fieldValues?.registryReference
    || record.location
    || SOLUTION_CATALOG[record.solutionCode]?.scope
    || 'unassigned';
}

function periodForRecord(record) {
  if (record.fieldValues?.period) return record.fieldValues.period;
  if (record.fiscalYear) return `FY${record.fiscalYear}`;
  return `${record.activePeriod?.startDate || ''}:${record.activePeriod?.endDate || ''}`;
}

/** Return the business duplicate key for one action. */
export function actionDuplicateKey(action) {
  if (action.solutionCode === 'retired_offset' && action.fieldValues?.registryReference) {
    return [action.solutionCode, boundaryForRecord(action)].join('|');
  }
  return [action.solutionCode, boundaryForRecord(action), periodForRecord(action)].join('|');
}

/** Return the same business duplicate key for an AI recommendation. */
export function recommendationDuplicateKey(recommendation) {
  if (recommendation.solutionCode === 'retired_offset'
    && recommendation.fieldValues?.registryReference) {
    return [recommendation.solutionCode, boundaryForRecord(recommendation)].join('|');
  }
  return [recommendation.solutionCode, boundaryForRecord(recommendation), periodForRecord(recommendation)].join('|');
}

function recommendationSnapshot(recommendation) {
  const concreteContextFields = [
    'why', 'dataUsed', 'formula', 'assumptions', 'missingData',
    'prerequisites', 'accounting', 'dataFreshness',
  ];
  const concreteContext = Object.fromEntries(concreteContextFields
    .filter(fieldName => recommendation[fieldName] !== undefined)
    .map(fieldName => [
      fieldName,
      JSON.parse(JSON.stringify(recommendation[fieldName])),
    ]));
  return deepFreeze({
    recommendationId: recommendation.id,
    hotspotId: recommendation.hotspotId || null,
    rank: numberOrNull(recommendation.rank),
    rankingBasis: recommendation.rankingBasis || null,
    translationKey: recommendation.translationKey,
    location: recommendation.location,
    boundary: boundaryForRecord(recommendation),
    boundaryId: recommendation.boundaryId || recommendation.boundary_id || null,
    boundaryName: recommendation.boundaryName || recommendation.boundary_name
      || recommendation.location || null,
    boundaryAliases: [...(recommendation.boundaryAliases
      || recommendation.boundary_aliases || [])],
    fiscalYear: recommendation.fiscalYear,
    period: periodForRecord(recommendation),
    scope: recommendation.scope || solutionMetadata(recommendation.solutionCode).scopeGroup
      || SOLUTION_CATALOG[recommendation.solutionCode].scope,
    category: recommendation.category || solutionMetadata(recommendation.solutionCode).category,
    annualKwh: numberOrNull(recommendation.annualKwh),
    emissionFactorTco2ePerKwh: numberOrNull(recommendation.emissionFactorTco2ePerKwh),
    renewable: typeof recommendation.renewable === 'boolean'
      ? recommendation.renewable : null,
    whyRecommended: recommendation.whyRecommended || recommendation.why,
    originalHotspot: recommendation.boundaryName || recommendation.location,
    originalPotentialImpactTco2e: recommendation.potentialImpact,
    expectedBenefit: recommendation.expectedBenefit || null,
    activityEquivalent: recommendation.activityEquivalent || null,
    financialEstimate: recommendation.financialEstimate
      ? JSON.parse(JSON.stringify(recommendation.financialEstimate))
      : null,
    potentialImpact: recommendation.potentialImpact,
    readiness: recommendation.readiness,
    fieldValues: { ...(recommendation.fieldValues || {}) },
    ...concreteContext,
  });
}

/** Save one recommendation as an action without creating a duplicate. */
export function appendRecommendationAction(actions, recommendation, updatedAt) {
  const duplicateKey = recommendationDuplicateKey(recommendation);
  const existingAction = actions.find(action => (
    action.sourceRecommendationId === recommendation.id
    || actionDuplicateKey(action) === duplicateKey
  ));
  if (existingAction) return actions;
  const fiscalYear = Number(recommendation.fiscalYear);
  const actionType = canonicalActionType({
    actionType: recommendation.actionType,
    accountingClassification: recommendation.accountingClassification
      || solutionMetadata(recommendation.solutionCode).accountingClassification,
    solutionCode: recommendation.solutionCode,
  });
  const currentFinancialEstimate = normalizeRecommendationFinance(
    recommendation.financialEstimate || {},
    recommendation.potentialImpact,
  );
  const metadata = solutionMetadata(recommendation.solutionCode);
  const actionBoundary = boundaryForRecord(recommendation);
  const action = {
    id: `action-${recommendation.id}`,
    solutionCode: recommendation.solutionCode,
    source: 'ai',
    sourceRecommendationId: recommendation.id,
    plannedActionKey: recommendation.plannedActionKey,
    solutionName: recommendation.recommendedAction || recommendation.solutionName || null,
    scope: recommendation.scope || recommendation.scopeGroup || metadata.scopeGroup,
    scopeGroup: recommendation.scopeGroup || recommendation.scope || metadata.scopeGroup,
    category: recommendation.category || metadata.category,
    subcategory: recommendation.subcategory || null,
    boundary: actionBoundary,
    boundaryId: recommendation.boundaryId || recommendation.boundary_id || null,
    boundaryName: recommendation.boundaryName || recommendation.boundary_name
      || recommendation.location || actionBoundary,
    sourceHotspotId: recommendation.hotspotId || null,
    accountingClassification: recommendation.accountingClassification
      || metadata.accountingClassification,
    actionType,
    verificationStatus: CLIMATE_ACTION_VERIFICATION_STATUS.NOT_SUBMITTED,
    recommendationSnapshot: recommendationSnapshot(recommendation),
    owner: null,
    dueDate: null,
    activePeriod: { startDate: `${fiscalYear}-01-01`, endDate: `${fiscalYear}-12-31` },
    workflowStatus: CLIMATE_ACTION_WORKFLOW_STATUS.DRAFT,
    actionStatus: CLIMATE_ACTION_WORKFLOW_STATUS.DRAFT,
    status: PRESENTATION_STATUS.DRAFT,
    readiness: recommendation.readiness,
    updatedAt,
    fiscalYear,
    annualResults: [{
      ...emptyAnnualResult(fiscalYear),
      estimatedImpactTco2e: numberOrNull(recommendation.potentialImpact),
      financialEstimate: currentFinancialEstimate,
    }],
    history: [{
      id: `action-${recommendation.id}-history-1`,
      fromStatus: null,
      toStatus: CLIMATE_ACTION_WORKFLOW_STATUS.DRAFT,
      reason: null,
      reasonKey: 'aiSaved',
      actor: null,
      actorKey: 'aiRecommendation',
      occurredAt: updatedAt,
    }],
    fieldValues: { ...(recommendation.fieldValues || {}) },
  };
  delete action.activePeriod;
  return [...actions, syncAnnualResultCompatibility(action)];
}

/** Record one dismissed candidate while preserving the previous value for Undo. */
export function dismissRecommendationInState(state, recommendationId, feedback = 'NOT_RELEVANT') {
  if (state.dismissedRecommendationIds.includes(recommendationId)) return state;
  const structuredFeedback = feedback && typeof feedback === 'object'
    ? {
      reason: String(feedback.reason || 'NOT_RELEVANT').trim(),
      detail: feedback.detail === null || feedback.detail === undefined
        ? null
        : String(feedback.detail).trim(),
    }
    : { reason: String(feedback || 'NOT_RELEVANT').trim(), detail: null };
  const dismissal = {
    recommendationId,
    ...structuredFeedback,
  };
  return {
    ...state,
    dismissedRecommendationIds: [...state.dismissedRecommendationIds, recommendationId],
    dismissedRecommendations: [...(state.dismissedRecommendations || []), dismissal],
    lastDismissedRecommendationId: recommendationId,
    lastDismissedRecommendation: dismissal,
  };
}

/** Restore the most recently dismissed recommendation. */
export function undoLastDismissedRecommendation(state) {
  if (!state.lastDismissedRecommendationId) return state;
  return {
    ...state,
    dismissedRecommendationIds: state.dismissedRecommendationIds.filter(
      recommendationId => recommendationId !== state.lastDismissedRecommendationId,
    ),
    dismissedRecommendations: (state.dismissedRecommendations || []).filter(
      dismissal => dismissal.recommendationId !== state.lastDismissedRecommendationId,
    ),
    lastDismissedRecommendationId: null,
    lastDismissedRecommendation: null,
  };
}

function initialManualAction(actionDraft, actionId, updatedAt) {
  const fiscalYear = Number(actionDraft.fiscalYear);
  if (!Number.isInteger(fiscalYear)) throw new RangeError('A fiscal year is required.');
  const legacyAction = {
    ...actionDraft,
    fiscalYear,
    impactRecords: (actionDraft.impactRecords || []).map(record => ({ ...record })),
    evidenceRecords: (actionDraft.evidenceRecords || []).map(record => ({ ...record })),
  };
  let annualResults = (actionDraft.annualResults || [])
    .map(normalizeAnnualResult)
    .filter(result => result.fiscalYear === fiscalYear);
  if (annualResults.length === 0) {
    const legacyResult = legacyAnnualResultForYear(legacyAction, fiscalYear);
    const calculatedImpact = calculateDemoActionImpact(actionDraft);
    annualResults = [legacyResult || {
      ...emptyAnnualResult(fiscalYear),
      estimatedImpactTco2e: calculatedImpact,
    }];
  }
  annualResults = annualResults.map(result => ({
    ...result,
    verifiedImpactTco2e: null,
    verificationStatus: CLIMATE_ACTION_VERIFICATION_STATUS.NOT_SUBMITTED,
  }));
  const actionType = canonicalActionType(actionDraft);
  const action = {
    ...actionDraft,
    id: actionId,
    source: actionDraft.source || 'manual',
    actionType,
    scope: actionDraft.scope,
    scopeGroup: actionDraft.scopeGroup || actionDraft.scope,
    category: actionDraft.category,
    subcategory: actionDraft.subcategory || null,
    accountingClassification: actionType === ACTION_TYPES.CARBON_CREDIT_RETIREMENT
      ? ACCOUNTING_CLASSIFICATIONS.RETIRED_CARBON_CREDIT
      : accountingClassificationForAction(actionDraft),
    owner: actionDraft.owner ? { ...actionDraft.owner } : { ...DEFAULT_ACTION_OWNER },
    dueDate: actionDraft.dueDate || `${fiscalYear}-12-31`,
    activePeriod: actionDraft.activePeriod
      ? { ...actionDraft.activePeriod }
      : { startDate: `${fiscalYear}-01-01`, endDate: `${fiscalYear}-12-31` },
    fiscalYear,
    workflowStatus: CLIMATE_ACTION_WORKFLOW_STATUS.DRAFT,
    actionStatus: CLIMATE_ACTION_WORKFLOW_STATUS.DRAFT,
    status: PRESENTATION_STATUS.DRAFT,
    annualResults,
    history: [{
      id: `${actionId}-history-1`,
      fromStatus: null,
      toStatus: CLIMATE_ACTION_WORKFLOW_STATUS.DRAFT,
      reason: null,
      reasonKey: 'manualCreated',
      actor: actionDraft.owner?.name || DEFAULT_ACTION_OWNER.name,
      occurredAt: updatedAt,
    }],
    updatedAt,
  };
  delete action.activePeriod;
  const canonicalAction = syncAnnualResultCompatibility(action);
  canonicalAction.readiness = readinessForWorkflow(canonicalAction, canonicalAction.workflowStatus);
  return canonicalAction;
}

function canonicalAnnualResultsForAction(action) {
  if ((action.annualResults || []).length > 0) {
    return action.annualResults.map(normalizeAnnualResult);
  }
  const fiscalYears = [...new Set((action.impactRecords || [])
    .map(record => Number(record.fiscalYear ?? record.year)))]
    .filter(Number.isInteger);
  return fiscalYears.map(fiscalYear => annualResultForYear(action, fiscalYear)).filter(Boolean);
}

function invalidatedAnnualResult(annualResult) {
  const verificationStatus = annualResult.reportedImpactTco2e === null
    ? CLIMATE_ACTION_VERIFICATION_STATUS.NOT_SUBMITTED
    : CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW;
  return {
    ...annualResult,
    verifiedImpactTco2e: null,
    verificationStatus,
    evidenceRecords: annualResult.evidenceRecords.map(record => ({
      ...record,
      status: verificationStatus,
    })),
  };
}

function invalidateAnnualVerifications(action, shouldInvalidate, updatedAt, reasonKey) {
  const invalidatedFiscalYears = [];
  const annualResults = canonicalAnnualResultsForAction(action).map((annualResult) => {
    const isVerified = annualResult.verificationStatus
      === CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED;
    const hasVerifiedRetirementBatch = isRetiredCarbonCreditAction(action)
      && (annualResult.retirementBatches || []).some(batch => (
        batch.verificationStatus === RETIREMENT_BATCH_STATUS.VERIFIED
      ));
    if ((!isVerified && !hasVerifiedRetirementBatch) || !shouldInvalidate(annualResult)) {
      return annualResult;
    }
    invalidatedFiscalYears.push(annualResult.fiscalYear);
    if (hasVerifiedRetirementBatch) {
      return {
        ...annualResult,
        verifiedImpactTco2e: null,
        verificationStatus: CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW,
        retirementBatches: annualResult.retirementBatches.map(batch => (
          batch.verificationStatus === RETIREMENT_BATCH_STATUS.VERIFIED
            ? {
              ...batch,
              verificationStatus: RETIREMENT_BATCH_STATUS.UNDER_REVIEW,
              evidenceRecords: (batch.evidenceRecords || []).map(record => ({
                ...record, status: RETIREMENT_BATCH_STATUS.UNDER_REVIEW,
              })),
              audit: [...(batch.audit || []), {
                type: 'VERIFICATION_INVALIDATED', actor: action.owner?.name || 'Demo Admin',
                occurredAt: updatedAt,
              }],
            }
            : batch
        )),
      };
    }
    return invalidatedAnnualResult(annualResult);
  });
  if (invalidatedFiscalYears.length === 0) return action;
  const historyEntries = invalidatedFiscalYears.map((fiscalYear, index) => ({
    id: `${action.id}-history-${(action.history || []).length + index + 1}`,
    historyType: 'VERIFICATION',
    fiscalYear,
    fromVerificationStatus: CLIMATE_ACTION_VERIFICATION_STATUS.VERIFIED,
    toVerificationStatus: CLIMATE_ACTION_VERIFICATION_STATUS.UNDER_REVIEW,
    reason: null,
    reasonKey,
    actor: action.owner?.name || DEFAULT_ACTION_OWNER.name,
    occurredAt: updatedAt,
  }));
  return syncAnnualResultCompatibility({
    ...action,
    annualResults,
    rejectionReason: null,
    history: [...(action.history || []), ...historyEntries],
  });
}

function invalidateAnnualVerification(action, fiscalYear, updatedAt) {
  return invalidateAnnualVerifications(
    action,
    annualResult => annualResult.fiscalYear === Number(fiscalYear),
    updatedAt,
    'verificationInvalidated',
  );
}

function invalidateAllAnnualVerifications(action, updatedAt) {
  return invalidateAnnualVerifications(
    action,
    () => true,
    updatedAt,
    'verificationInvalidatedByMasterEdit',
  );
}

function editExistingAction(existingAction, actionDraft, updatedAt) {
  const masterCriticalEdit = changesMasterVerificationCriticalFields(existingAction, actionDraft);
  const annualCriticalFiscalYears = changedAnnualResultFiscalYears(existingAction, actionDraft);
  const ownerWasProvided = Object.prototype.hasOwnProperty.call(actionDraft, 'owner');
  const nextOwner = ownerWasProvided ? actionDraft.owner : existingAction.owner;
  const snapshotWasProvided = Object.prototype.hasOwnProperty.call(
    actionDraft,
    'recommendationSnapshot',
  );
  const selectedYear = Number(existingAction.fiscalYear);
  const actionType = canonicalActionType(existingAction);
  if (Object.prototype.hasOwnProperty.call(actionDraft, 'actionType')
    && canonicalActionType(actionDraft) !== actionType) {
    throw new RangeError('ACTION_TYPE_IS_IMMUTABLE');
  }
  let editedAction = {
    ...existingAction,
    ...actionDraft,
    id: existingAction.id,
    source: existingAction.source,
    owner: nextOwner ? { ...nextOwner } : null,
    actionType,
    scope: actionDraft.scope ?? existingAction.scope,
    scopeGroup: actionDraft.scopeGroup || actionDraft.scope || existingAction.scopeGroup,
    category: actionDraft.category ?? existingAction.category,
    subcategory: actionDraft.subcategory ?? existingAction.subcategory ?? null,
    accountingClassification: actionType === ACTION_TYPES.CARBON_CREDIT_RETIREMENT
      ? ACCOUNTING_CLASSIFICATIONS.RETIRED_CARBON_CREDIT
      : accountingClassificationForAction({ ...existingAction, ...actionDraft, actionType }),
    fiscalYear: selectedYear,
    actionStatus: canonicalWorkflowStatus(
      actionDraft.workflowStatus || existingAction.workflowStatus || existingAction.status,
    ),
    annualResults: (actionDraft.annualResults || existingAction.annualResults || [])
      .map(normalizeAnnualResult)
      .filter(result => result.fiscalYear === selectedYear),
    history: (existingAction.history || []).map(historyEntry => ({ ...historyEntry })),
    recommendationSnapshot: snapshotWasProvided
      ? actionDraft.recommendationSnapshot
      : existingAction.recommendationSnapshot,
    updatedAt,
  };
  delete editedAction.activePeriod;
  delete editedAction.actionKind;
  delete editedAction.creditAllocationScope;
  delete editedAction.isApprovedForCompensation;
  delete editedAction.requestedForCompensationReview;
  delete editedAction.reviewStatus;
  const hasLegacyAnnualEdit = ['impactRecords', 'evidenceRecords'].some(fieldName => (
    Object.prototype.hasOwnProperty.call(actionDraft, fieldName)
  ));
  if (!actionDraft.annualResults && hasLegacyAnnualEdit) {
    const legacyResult = legacyAnnualResultForYear(actionDraft, selectedYear);
    if (legacyResult) editedAction = upsertAnnualResult(editedAction, selectedYear, legacyResult);
  }
  editedAction = syncAnnualResultCompatibility(editedAction);
  if (masterCriticalEdit) {
    editedAction = invalidateAllAnnualVerifications(editedAction, updatedAt);
  } else {
    annualCriticalFiscalYears.forEach((fiscalYear) => {
      editedAction = invalidateAnnualVerification(editedAction, fiscalYear, updatedAt);
    });
  }
  editedAction.readiness = readinessForWorkflow(editedAction, editedAction.workflowStatus);
  return editedAction;
}

function assertUniqueRetirementIdentities(actions = []) {
  const seenReferences = new Set();
  const seenSerialRanges = new Set();
  actions.forEach((action) => {
    if (!isRetiredCarbonCreditAction(action)) return;
    (action.annualResults || []).forEach((annualResult) => {
      normalizeRetirementBatches(annualResult, action).forEach((batch) => {
        const reference = String(batch.retirementReference || '').trim().toLowerCase();
        const serialRange = String(batch.serialRange || '').trim().toLowerCase();
        if ((reference && seenReferences.has(reference))
          || (serialRange && seenSerialRanges.has(serialRange))) {
          throw new RangeError('DUPLICATE_RETIREMENT_IDENTITY');
        }
        if (reference) seenReferences.add(reference);
        if (serialRange) seenSerialRanges.add(serialRange);
      });
    });
  });
}

/** Insert or edit one in-memory action while enforcing duplicates and verification rules. */
export function upsertClimateAction(actions, actionDraft, updatedAt) {
  const existingAction = actions.find(action => action.id === actionDraft.id);
  if (existingAction) {
    const editedAction = editExistingAction(existingAction, actionDraft, updatedAt);
    if (actions.some(action => (
      action.id !== existingAction.id
      && actionDuplicateKey(action) === actionDuplicateKey(editedAction)
    ))) return actions;
    const nextActions = actions.map(action => (
      action.id === existingAction.id ? editedAction : action
    ));
    assertUniqueRetirementIdentities(nextActions);
    return nextActions;
  }
  const actionId = actionDraft.id || `action-manual-${actions.length + 1}`;
  const newAction = initialManualAction(actionDraft, actionId, updatedAt);
  if (actions.some(action => actionDuplicateKey(action) === actionDuplicateKey(newAction))) {
    return actions;
  }
  const nextActions = [...actions, newAction];
  assertUniqueRetirementIdentities(nextActions);
  return nextActions;
}

/**
 * Drafts are the only actions that may be removed from the Action Plan.
 * Once delivery starts, the action and its FY evidence must remain available for audit.
 */
export function canRemoveClimateAction(action = {}) {
  return canonicalWorkflowStatus(
    action.workflowStatus || action.actionStatus || action.status,
  ) === CLIMATE_ACTION_WORKFLOW_STATUS.DRAFT;
}

/** Remove one draft from the in-memory demo without mutating the source collection. */
export function removeClimateAction(actions = [], actionId) {
  const action = actions.find(candidate => candidate.id === actionId);
  if (!action || !canRemoveClimateAction(action)) return actions;
  return actions.filter(candidate => candidate.id !== actionId);
}

/** Return a fresh state object so demo mutations never modify shared fixtures. */
export function createClimateActionDemoState() {
  const targets = Object.fromEntries(Object.entries(DEMO_TARGETS).map(([scope, target]) => [
    scope,
    {
      ...target,
      milestones: target.milestones?.map(milestone => ({ ...milestone })),
    },
  ]));
  return {
    selectedYear: DEFAULT_DEMO_YEAR,
    targets,
    targetCycles: normalizeTargetCycles(DEMO_TARGET_CYCLES, targets).map(cycle => ({
      ...cycle,
      targets: cycle.targets
        ? Object.fromEntries(Object.entries(cycle.targets).map(([scope, target]) => [
          scope,
          { ...target, milestones: target.milestones?.map(milestone => ({ ...milestone })) },
        ]))
        : null,
    })),
    actions: DEMO_ACTIONS.map(canonicalizeDemoAction),
    recommendations: DEMO_RECOMMENDATIONS.map(recommendation => ({
      ...recommendation,
      boundaryAliases: [...(recommendation.boundaryAliases || [])],
      fieldValues: { ...(recommendation.fieldValues || {}) },
    })),
    dismissedRecommendationIds: [],
    dismissedRecommendations: [],
    lastDismissedRecommendationId: null,
    lastDismissedRecommendation: null,
  };
}
